/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  *
  * Claude usage for every pr-review run at month, week or day grain, as an ANALYSIS
  * surface: regroupable by any
  * dimension, narrowable by clicking a value, exportable at two grains, and accompanied by a
  * derivation the viewer can read instead of trusting.
  *
  * Renders as the last TAB of the Control Panel, so it deliberately has no Page/Header of its
  * own -- the shell supplies those. Control Panel itself is open to any authenticated user, so
  * the tab is gated separately on ClaudeMetricsReadPermission (admin-only per the clause in
  * permissionPolicyExtension.ts): the surface must not merely be hard to find.
  *
  * Aggregation lives in claudeMetricsAnalysis.ts and claudeMetricsView.ts as pure functions;
  * this file is composition, state and presentation.
  */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InfoCard, Progress, WarningPanel } from '@backstage/core-components';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import GetAppIcon from '@material-ui/icons/GetApp';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import RefreshIcon from '@material-ui/icons/Refresh';
import PublicIcon from '@material-ui/icons/Public';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightSmallIcon from '@material-ui/icons/ChevronRight';
import { useClaudeMetrics } from './useClaudeMetrics';
import { useClaudeActivity } from './useClaudeActivity';
import { useClaudeReviewQuality } from './useClaudeReviewQuality';
import {
  ReviewQualitySummaryStrip,
  RunFindingsTable,
} from './ReviewFindingRows';
import { RunQueryFilters } from './usePrRuns';
import {
  PeriodScope,
  REVIEW_JOINABLE,
  ReviewConstraint,
  TeamedFinding,
  attachReviewQuality,
  findingsForPath,
  makePeriodFilter,
  matchesConstraint,
  reviewWithheldReason,
  windowCoversPeriod,
} from './claudeReviewQualityJoin';
import { HowItWorks } from '../common/HowItWorks';
import {
  DAY_DIMENSIONS,
  DIMENSION_LABELS,
  Dimension,
  GroupNode,
  Sort,
  SortKey,
  deltasByPath,
  describeDerivation,
  exportFilename,
  filterGroups,
  formatDelta,
  groupRows,
  resortTree,
  toCsvGrain,
  toCsvShownView,
} from './claudeMetricsAnalysis';
import {
  Period,
  Totals,
  UNKNOWN_ACTOR,
  REVIEW_JOB,
  displayOwner,
  WeeklyRow,
  effectiveMaxOffset,
  formatTokens,
  reportingStatus as statusHeadline,
  reportedCost,
  reportedSavings,
  SUPERSEDED_JOB,
  supersededShare,
  describeSavingsMethod,
  isCoverageMethod,
  totals,
  ActivityDay,
  ANTHROPIC_TZ,
  browserTz,
  githubUrl,
  loadTz,
  reanchorDay,
  reanchorMonth,
  saveTz,
  tzOptions,
  tzShortName,
  monthOfDay,
  shiftDay,
  shiftMonth,
  thisMonthIn,
  todayIn,
  silences,
  utcToday,
  formatUsd,
  weeksBack,
} from './claudeMetricsView';

/** A value the analyst has clicked to narrow to. Composable across dimensions. */
type Focus = { dim: Dimension; value: string };

const useStyles = makeStyles(theme => ({
  bar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  // The command rail: ONE toolbar, three anchored zones (period instrument /
  // grouping / actions) balanced by space-between instead of piling left. Sticky:
  // the table header already survives scrolling, this completes the frame so time
  // and grouping controls never leave the analyst mid-scroll.
  rail: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.25, 0),
    marginBottom: theme.spacing(1.5),
    // NARROW VIEWPORTS: stop being sticky.
    //
    // Sticky is right on a desktop, where the rail is one 61px row and keeping the
    // period and grouping controls in reach while the table scrolls is worth 61px.
    // At 390px the same rail wraps to seven rows -- measured 297px, 35% of an
    // 844px phone -- and sticky means that third of the screen is never given
    // back. The controls stay fully present and reachable; they just scroll away
    // like content, which is the trade a phone actually wants.
    //
    // Deliberately NOT a disclosure: hiding the secondary controls behind a
    // toggle would add an interaction to design, label and keyboard-test in order
    // to save space that scrolling already returns.
    [theme.breakpoints.down('sm')]: {
      position: 'static',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 0),
    },
  },
  railZone: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  // The segmented Month|Week|Day granularity toggle, fused into the period
  // instrument so nav, granularity and date read as one control.
  segmented: {
    height: 40,
    '& button': {
      textTransform: 'none',
      padding: theme.spacing(0.5, 1.75),
    },
  },
  barSpacer: { flexGrow: 1 },
  weekLabel: { minWidth: 216, whiteSpace: 'nowrap' },
  control: { minWidth: 132 },
  // The dimension pickers: wide enough for 'Run trigger' plus the popup icon.
  // 152, not 185: the rail carries a zone control now, and the longest label
  // ('Run trigger') still clears the popup icon at this width. Measured, not
  // guessed -- 185 pushed the export buttons onto a second row at 1860px.
  picker: { minWidth: 152 },
  // The reading zone is PROVENANCE, not a filter: it states the basis every day and
  // month boundary is cut on. So it reads as an indicator rather than a third
  // combobox -- a globe, the zone's abbreviation, and nothing else.
  //
  // The trigger shows the ABBREVIATION ('UTC', 'CDT'), which is why 104px was wrong:
  // it was trying to hold a full IANA name and truncated both the value and the
  // options. Four characters fit here with room to spare.
  tzPicker: {
    width: 104,
    '& [class*="inputRoot"]': { paddingLeft: theme.spacing(0.75) },
  },
  tzGlobe: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.25),
    flexShrink: 0,
  },
  // The popup is sized for its CONTENT, not for the trigger. MUI matches the popper
  // to the anchor's width by inline style, which is what clipped 'America/Chicago
  // (local)' to 'America/C...'; overriding it is the fix, and it has to win against
  // that inline width.
  tzPopper: { width: '328px !important' },
  tzOption: {
    fontSize: 13,
    // Long zone names wrap rather than truncate: the whole point of this pass.
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  // Data-density layer, spaced by GROUP rather than by row.
  //
  // The first cut gave every row an identical hairline and identical padding, so a repo and
  // its models read as one flat list: proximity carried no meaning and the borders were
  // compensating for it. Now a rule and generous space open each group, its children sit
  // tight and unlined beneath it, and the cadence itself says what belongs together.
  table: {
    // The unified ledger carries 16 columns; without a floor the label and
    // Team cells letter-stack instead of letting the container scroll.
    minWidth: 1360,
    '& td, & th': { fontVariantNumeric: 'tabular-nums', borderBottom: 'none' },
    // Neutralise the inherited row striping. It alternates per ROW, so it cuts across the
    // groups above and the eye follows the stripes instead of the grouping. Proximity is
    // doing that job here; two competing signals leave neither legible.
    // `&&` again: the striping arrives as a class-plus-pseudo rule (`.MuiTableRow-root
    // :nth-of-type(odd)`), which out-specifies a plain class-plus-element selector.
    '&& tbody tr': { backgroundColor: 'transparent' },
    // Inside a group: tight, so the block reads as one thing.
    '& tbody td': {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
    },
    // Sticky header: scrolling a long week must not cost the column names.
    // text.primary, not the MUI default head gray: that gray measured 4.4:1 on
    // this theme's card surface -- 0.1 under AA, sixteen cells at a time.
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap',
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(1),
    },
  },
  // Opens a group: a rule, generous space above and less below, so the row belongs to what
  // follows rather than floating between two groups. It is also the group's subtotal, which
  // is why the whole row carries the weight and not just one cell.
  groupStart: {
    // `&&` doubles the class for specificity: the table's own `& tbody td` padding rule is
    // more specific than a single class and was silently winning, so the group rhythm was
    // authored but not rendered.
    '&& > td': {
      borderTop: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(2.25),
      paddingBottom: theme.spacing(0.75),
      fontWeight: 600,
    },
    // The header already draws the first line; a second one here would double it.
    '&&:first-child > td': { borderTop: 'none', paddingTop: theme.spacing(1) },
  },
  // Labels recede with depth. The FIGURES stay full-ink: they are what gets scanned.
  // break-word, not 'anywhere': 'anywhere' shredded identifiers mid-token
  // ("claud e-opus-4-8") at narrow widths; break-word only breaks when a token
  // genuinely cannot fit.
  labelCell: { minWidth: 170, maxWidth: 340, whiteSpace: 'normal', overflowWrap: 'break-word' },
  labelDeep: { color: theme.palette.text.secondary },
  // Horizontal only: an inner vertical scroll fought the page's own scrollbar
  // (two competing thumbs, offset confusion) — the page owns vertical.
  scroll: { overflowX: 'auto' },
  num: { textAlign: 'right', whiteSpace: 'nowrap' },
  // The magnitude bar underlay: share-of-visible-total as width, behind the Total
  // figure. It ENCODES proportion (not decoration); width transitions with the
  // same ease the totals band rolls with, and stands still under reduced motion.
  barCell: { position: 'relative' },
  magnitudeBar: {
    position: 'absolute',
    left: 0,
    top: '15%',
    bottom: '15%',
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,110,91,0.16)'
        : 'rgba(140,67,81,0.12)',
    borderRadius: 2,
    transition: 'width 650ms cubic-bezier(0.22, 1, 0.36, 1)',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    pointerEvents: 'none',
  },
  barValue: { position: 'relative' },
  share: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1),
  },
  // Hover focus: the row under comparison keeps full ink while siblings recede
  // slightly -- the 11-column scan locks to one row without any color change.
  focusBody: {
    '&:hover tr': { opacity: 0.55, transition: 'opacity 120ms ease-out' },
    '&:hover tr:hover': { opacity: 1 },
  },
  // Sortable header: a real button (keyboard-first), inheriting the th's ink.
  sortButton: {
    font: 'inherit',
    color: 'inherit',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  sortArrow: { fontSize: '0.7em', marginLeft: 2 },
  // Collapse chevron on group rows: present, quiet, keyboard-reachable.
  collapseBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    marginRight: theme.spacing(0.5),
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    verticalAlign: 'middle',
    '& svg': { fontSize: '1rem', display: 'block' },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  // The four token-detail columns: present for the analyst who needs them, quiet so
  // the scan lands on Total, Cost and vs prev. Same principle as labelDeep --
  // hierarchy by ink, not by hiding.
  numQuiet: {
    textAlign: 'right',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
  },
  // Values are buttons, not hover-only affordances: keyboard reachable, visibly focusable.
  focusable: {
    font: 'inherit',
    color: 'inherit',
    background: 'none',
    border: 'none',
    padding: 0,
    textAlign: 'left',
    cursor: 'pointer',
    textDecoration: 'underline dotted',
    textUnderlineOffset: 3,
    '&:hover': { color: theme.palette.primary.main },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  // Delta colours, measured against the surface these actually sit on -- the card
  // (#262B2A in dark, #F1EDE3 in light), NOT the body. The theme's own error.main fails AA
  // there in dark mode at 3.61:1, so dark uses the brand accent instead, and the green is
  // lightened for margin. Ratios below are computed, not estimated:
  //   dark   up #FF6E5B on #262B2A = 5.22:1   down #8fb06f = 5.89:1
  //   light  up #8c4351 on #F1EDE3 = 5.92:1   down #485e30 = 6.15:1
  // The sign (+/-) carries the meaning regardless, so colour is reinforcement, not the
  // only channel.
  up: {
    color: theme.palette.type === 'dark' ? '#FF6E5B' : theme.palette.error.main,
  },
  down: {
    color: theme.palette.type === 'dark' ? '#8fb06f' : theme.palette.success.main,
  },
  quiet: { color: theme.palette.text.secondary },
  teamCell: { minWidth: 170, overflowWrap: 'normal', wordBreak: 'normal' },
  reviewDrillCell: {
    background: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  // Two tiers, chunked by LAYOUT as well as ink: the decision numbers (totals,
  // costs, trend) read as one generous row; the token components sit beneath in
  // a tighter, quieter band. One generous interval between them IS the rhythm.
  // The terminal band: metrics distributed edge to edge, each cell separated by a
  // hairline so the full page width reads as one instrument panel. flex: 1 on the
  // cells is what makes the numbers OWN the length of the page rather than
  // clustering left with dead space right.
  // A border-left between flex items expresses COLUMNS. The moment the band wraps
  // it is no longer columns, and the rule becomes wrong rather than merely tight:
  // the first cell of row 2 is still `* + *`, so it kept a hairline with nothing to
  // its left, and its paddingLeft pushed its figure out of line with row 1's flush
  // first cell. Both were measured with a probe at 340px.
  //
  // So the rules apply only where the band is genuinely one row, and spacing is
  // carried by gap everywhere -- gap cannot produce a leading separator, and it
  // indents no one, which is what fixes the ragged left edge.
  summaryPrimary: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    width: '100%',
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(2.5),
    '& > *': { flex: '1 1 0', minWidth: 150 },
    [theme.breakpoints.up('md')]: {
      // Four cells at minWidth 150 fit well inside md, so this is the one-row
      // case. columnGap yields to the padding so the desktop rhythm is unchanged.
      columnGap: 0,
      '& > * + *': {
        borderLeft: `1px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(3),
      },
    },
  },
  summarySecondary: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    width: '100%',
    marginTop: theme.spacing(3),
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(2),
    '& > *': { flex: '1 1 0', minWidth: 96 },
    [theme.breakpoints.up('md')]: {
      columnGap: 0,
      '& > * + *': { paddingLeft: theme.spacing(3) },
    },
  },
  // column-reverse: dt first in the DOM for the pairing, figure first on screen
  // for the hierarchy. `margin: 0` on dd is load-bearing -- browsers give it a
  // 40px left margin by default, which would indent every figure.
  metric: {
    display: 'flex',
    flexDirection: 'column-reverse',
    margin: 0,
    '& dd': { margin: 0 },
  },
  // The savings tile's per-method breakdown: a micro-ledger, not prose. Grid
  // columns keep the figures aligned (tabular-nums) so several methods read
  // as one small table under the tile's value; the dollars carry the weight,
  // names and run counts stay quiet.
  methodRows: {
    marginTop: theme.spacing(1),
    display: 'grid',
    gridTemplateColumns: 'repeat(4, max-content)',
    columnGap: theme.spacing(1.5),
    rowGap: 2,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    alignItems: 'baseline',
  },
  methodName: { color: theme.palette.text.secondary },
  methodUsd: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 500,
  },
  methodRuns: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
  },
  // Coverage rows share the dollars column but must not read as dollars:
  // italic secondary ink so the eye skips them when scanning the ledger.
  methodCoverage: {
    textAlign: 'right',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  methodMore: { gridColumn: '1 / -1', color: theme.palette.text.secondary },
  // The cache-read discount annotation: quieter and smaller than the token
  // figure it rides on — an aside about pricing, not a metric of its own.
  cacheDiscountNote: {
    color: theme.palette.text.secondary,
    fontSize: '0.8em',
    fontWeight: 400,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  metricValue: {
    fontSize: '2.125rem',
    fontWeight: 550,
    lineHeight: 1.15,
    letterSpacing: '-0.015em',
    // tabular-nums is what lets the count-up roll without horizontal jitter.
    fontVariantNumeric: 'tabular-nums',
  },
  derivation: {
    '& dt': {
      fontWeight: 600,
      marginTop: theme.spacing(1.5),
      fontSize: '0.8125rem',
    },
    '& dd': { margin: 0, color: theme.palette.text.secondary, fontSize: '0.8125rem' },
    // Prose stays readable; the tables above are allowed to run wide.
    maxWidth: '72ch',
  },
  reduced: {
    '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms !important' },
  },
  // The reload sweep. While a fetch is in flight the CURRENT answer stays on screen
  // at reduced ink (stale-while-revalidate in the hook) and brightens as the fresh
  // numbers roll in -- the old figures are the from-state of the animation, so the
  // page never collapses to a spinner and rebuilds. Opacity only: this region holds
  // hundreds of table cells, so filters are off-budget.
  content: {
    transition: 'opacity 240ms cubic-bezier(0.16, 1, 0.3, 1)',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  reloading: {
    opacity: 0.45,
    // Half-visible rows must not be half-clickable: a narrowing click landing on
    // data that is about to be replaced would narrow the WRONG period.
    pointerEvents: 'none',
  },
  // Fresh data -- or a client-side regroup -- settles the table into place: a short
  // rise from already-visible, never an entrance from nothing.
  '@keyframes settle': {
    from: { opacity: 0.35, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'none' },
  },
  settleIn: {
    animation: '$settle 260ms cubic-bezier(0.16, 1, 0.3, 1)',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  // The refresh control acknowledges its own click: it spins for as long as any
  // fetch is in flight, disabled state notwithstanding.
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  spinning: {
    animation: '$spin 900ms linear infinite',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  // A stale repo is a WARNING state: the chip now wears the same computed-
  // contrast alarm ink the deltas use (label text still carries the meaning --
  // color reinforces), and gets the vertical inset the detector measured at 2px.
  quietChip: {
    color: theme.palette.type === 'dark' ? '#FF6E5B' : theme.palette.error.main,
    borderColor: theme.palette.type === 'dark' ? '#FF6E5B' : theme.palette.error.main,
    height: 26,
    '& .v5-MuiChip-label, & [class*="MuiChip-label"]': {
      paddingTop: 2,
      paddingBottom: 2,
    },
  },
  // The external-link affordance beside a PR value: present but quiet, so the
  // narrow action (the panel's primary value-click grammar) stays the headline.
  prLink: {
    marginLeft: theme.spacing(0.75),
    verticalAlign: 'text-bottom',
    color: theme.palette.text.secondary,
    '& svg': { fontSize: '0.9rem' },
    '&:hover': { color: theme.palette.primary.main },
  },
  // The trust table is three short columns; at full card width they drifted a
  // desert apart (repo hard left, date/status hard right). Capping the measure
  // keeps each row readable as one fact.
  statusTable: { maxWidth: 760 },
  // The trust card splits: table left (its capped measure keeps rows readable),
  // ingest chart right, filling the width the table deliberately leaves.
  statusSplit: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
    alignItems: 'flex-start',
  },
  statusTableZone: { flex: '1 1 480px', minWidth: 320 },
  activityBlock: { flex: '1 1 320px', minWidth: 280 },
  // The scrub readout: a FIXED line above the bars that rewrites as the pointer
  // sweeps -- one steady place to read, instead of thirty flickering tooltips.
  activityReadout: {
    marginTop: theme.spacing(1),
    fontSize: '0.8125rem',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.text.primary,
  },
  activityBars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 2,
    height: 120,
    marginTop: theme.spacing(1),
    // While scrubbing, the swept bar keeps full ink and siblings recede --
    // the same focus grammar the Breakdown's hover uses.
    '&$activityScrubbing $activityBar:not($activityBarActive), &$activityScrubbing $activityZero:not($activityBarActive)':
      {
        opacity: 0.45,
      },
  },
  activityScrubbing: {},
  activityBarActive: {},
  // Bars are BUTTONS: the click-to-jump is real navigation, so it gets real
  // button semantics (Enter/Space, focusability) instead of a synthetic div.
  activityBar: {
    flex: '1 1 0',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,110,91,0.55)'
        : 'rgba(140,67,81,0.55)',
    borderRadius: 1,
    transformOrigin: 'bottom',
    transition: 'opacity 120ms ease-out',
    animation: '$activityBarIn 350ms cubic-bezier(0.22, 1, 0.36, 1) backwards',
    '&$activityBarActive, &:focus-visible': {
      backgroundColor:
        theme.palette.type === 'dark' ? '#FF6E5B' : theme.palette.error.main,
      outline: 'none',
    },
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' },
  },
  // A day with NO ingest: a flat tick in the alarm ink, never an empty gap --
  // silence must look like silence, not like a narrower chart. The ticks land
  // AFTER the bars in the draw-on: the alarm reads as punctuation, not noise.
  activityZero: {
    flex: '1 1 0',
    height: 2,
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    backgroundColor:
      theme.palette.type === 'dark' ? '#FF6E5B' : theme.palette.error.main,
    transition: 'opacity 120ms ease-out',
    animation: '$activityTickIn 200ms ease-out backwards',
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' },
  },
  // Today's bar breathes while the window is still filling -- one composited
  // opacity loop, stilled entirely under reduced motion.
  activityToday: {
    animation:
      '$activityBarIn 350ms cubic-bezier(0.22, 1, 0.36, 1) backwards, ' +
      '$activityPulse 3s ease-in-out 1.2s infinite',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  '@keyframes activityBarIn': {
    from: { transform: 'scaleY(0)' },
    to: { transform: 'scaleY(1)' },
  },
  '@keyframes activityTickIn': {
    from: { opacity: 0, transform: 'scaleX(0.4)' },
    to: { opacity: 1, transform: 'scaleX(1)' },
  },
  '@keyframes activityPulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.55 },
  },
  activityAxis: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    fontVariantNumeric: 'tabular-nums',
  },
  // The period cluster wraps as ONE unit on narrow screens, so a chevron never
  // orphans on its own line away from the label it flanks.
  navCluster: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    whiteSpace: 'nowrap',
  },
  // Screen-reader-only: table captions and the live region. The standard clip
  // recipe -- display:none would silence it entirely. Pinned to the origin:
  // an unanchored absolute box sits at its STATIC position (for the table
  // caption, deep inside the scrolled Breakdown), and with no positioned
  // ancestor it inflates the DOCUMENT's scroll height -- the phantom second
  // vertical scrollbar over 550px of empty page.
  srOnly: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    // The animated figures ship a screen-reader copy of their settled value, so a
    // selection across the totals band would otherwise put every number in the
    // clipboard twice. The visible copy stays selectable.
    userSelect: 'none',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  // The two decision numbers read first; the token components sit beneath at
  // reduced ink -- chunking the 9-metric wall the critique flagged.
  metricValueSecondary: {
    fontSize: '1.0625rem',
    fontWeight: 500,
    lineHeight: 1.2,
    fontVariantNumeric: 'tabular-nums',
    color: theme.palette.text.secondary,
  },
}));

/**
 * The band's one authored motion moment: on every period switch or narrowing the
 * numbers ROLL to their new value over ~650ms of cubic ease-out. First mount
 * renders the final value immediately (no arrival theatrics), reduced-motion
 * users get the instant jump, and tabular-nums upstream keeps the roll from
 * jittering horizontally. Interpolation happens on the NUMBER and each frame is
 * re-formatted, so "37.62M" rolls through real intermediate magnitudes rather
 * than lerping glyphs.
 */
const AnimatedNumber = ({
  value,
  format,
}: {
  value: number;
  format: (n: number) => string;
}) => {
  const classes = useStyles();
  const [shown, setShown] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const from = prev.current;
    prev.current = value;
    if (from === value) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(value);
      return undefined;
    }
    const start = performance.now();
    const duration = 650;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - (1 - p) ** 3;
      setShown(from + (value - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  // The rolling text is DECORATION as far as assistive tech is concerned: for
  // 650ms it holds interpolated figures (847.3M on the way to 1.40B), and this
  // product's first principle is to never show a number it cannot explain. The
  // settled value ships alongside it, screen-reader-only, so browsing the tile
  // mid-roll reads the truth. (The band's aria-live region already announces the
  // settled total on recompute; this covers the browse path it does not.)
  return (
    <>
      <span aria-hidden="true">{format(shown)}</span>
      <span className={classes.srOnly}>{format(value)}</span>
    </>
  );
};

/**
 * Share as a percentage, one decimal until it stops mattering ("5.9%", "23%").
 * A real-but-tiny share floors at "<0.1%" instead of rounding to "0.0%" — the
 * tile only exists when superseded rounds EXIST, so it must never display the
 * zero it just promised not to fabricate.
 */
const formatShare = (pct: number) => {
  if (pct > 0 && pct < 0.05) return '<0.1%';
  return `${pct.toFixed(pct >= 10 ? 0 : 1)}%`;
};

const Metric = ({
  label,
  value,
  hint,
  secondary,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  /** Token components and meta-counts: present, but not competing with the money. */
  secondary?: boolean;
  /** Delta coloring, same computed-contrast pair the table uses. Sign stays primary. */
  tone?: 'up' | 'down';
}) => {
  const classes = useStyles();
  // A definition list, not two loose Typographys: the figure and its label are a
  // PAIR, and nothing but reading order was expressing that. dt precedes dd in the
  // DOM (so it reads "total tokens: 1.40B") while column-reverse keeps the figure
  // on top, which is the visual hierarchy the band is built on.
  return (
    <Box component="dl" className={classes.metric}>
      <Typography component="dt" variant="caption" color="textSecondary">
        {label}
        {hint ? ` ${hint}` : ''}
      </Typography>
      <Typography
        component="dd"
        className={`${secondary ? classes.metricValueSecondary : classes.metricValue}${
          tone ? ` ${classes[tone]}` : ''
        }`}
      >
        {value}
      </Typography>
    </Box>
  );
};

/**
 * The trust chart as an INSTRUMENT: runs received per calendar day (viewer's
 * zone) for the last 30 days, zero-days marked in the alarm ink rather than
 * omitted. Sweeping the pointer scrubs a fixed readout above the bars (one
 * steady place to read, not thirty flickering tooltips); clicking a bar jumps
 * the panel to that Day view -- the chart is the fastest route to "what
 * happened Tuesday". Arrow keys scrub, Enter/Space jumps: the bars are real
 * buttons with a roving tabindex, so the keyboard path is native, not
 * simulated. On arrival the bars draw in left to right and the alarm ticks
 * land last; today's bar breathes while the window is still filling.
 */
const ActivityChart = ({
  days,
  onPickDay,
  tz,
}: {
  days: readonly ActivityDay[];
  onPickDay: (day: string) => void;
  tz: string;
}) => {
  const classes = useStyles();
  const [scrub, setScrub] = useState<number | null>(null);
  const barsRef = useRef<HTMLDivElement | null>(null);
  if (!days.length) return null;
  const max = Math.max(...days.map(d => d.runs), 1);
  const localToday = todayIn(tz);
  const at = scrub === null ? undefined : days[scrub];
  const describe = (d: ActivityDay) =>
    `${d.day} · ${d.runs} run${d.runs === 1 ? '' : 's'} · ${formatTokens(d.tokens)} tokens · ${d.repos} repo${d.repos === 1 ? '' : 's'}`;

  // Index from pointer position over the CONTAINER: bar gaps never drop the
  // scrub, so the sweep reads continuously like a scope cursor.
  const scrubFromPointer = (e: React.PointerEvent) => {
    const rect = barsRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return;
    const i = Math.floor(((e.clientX - rect.left) / rect.width) * days.length);
    setScrub(Math.max(0, Math.min(days.length - 1, i)));
  };

  const focusBar = (i: number) => {
    const el = barsRef.current?.querySelector<HTMLButtonElement>(`[data-index="${i}"]`);
    el?.focus();
  };

  return (
    <Box className={classes.activityBlock}>
      <Typography variant="subtitle2" component="h3">
        Ingest — last {days.length} days
      </Typography>
      <Typography variant="caption" color="textSecondary">
        runs received per day · a flat tick is a day with none · click a bar to
        open that day
      </Typography>
      <div className={classes.activityReadout} data-testid="activity-readout">
        {at
          ? describe(at)
          : `${days.length} days · ${days[0].day} → ${days[days.length - 1].day} · peak ${max}/day`}
      </div>
      <div
        ref={barsRef}
        className={`${classes.activityBars}${scrub !== null ? ` ${classes.activityScrubbing}` : ''}`}
        role="group"
        aria-label={`Runs received per day over the last ${days.length} days; peak ${max} in a day${
          days.some(d => d.runs === 0) ? '; some days received none' : ''
        }. Arrow keys scrub; Enter opens the day.`}
        onPointerMove={scrubFromPointer}
        onPointerLeave={() => setScrub(null)}
        onKeyDown={e => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          const from = scrub ?? days.length - 1;
          focusBar(
            Math.max(0, Math.min(days.length - 1, from + (e.key === 'ArrowRight' ? 1 : -1))),
          );
        }}
      >
        {days.map((d, i) => {
          const zero = d.runs === 0;
          const cls = [
            zero ? classes.activityZero : classes.activityBar,
            !zero && d.day === localToday ? classes.activityToday : '',
            i === scrub ? classes.activityBarActive : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={d.day}
              type="button"
              data-index={i}
              className={cls}
              style={{
                ...(zero ? {} : { height: `${Math.max(3, (d.runs / max) * 100)}%` }),
                // The draw-on: bars sweep left to right (~16ms apart), the
                // alarm ticks land together after them, as punctuation.
                animationDelay: zero ? `${500 + i * 8}ms` : `${i * 16}ms`,
              }}
              // Roving tabindex: one stop in the tab order (the scrubbed bar,
              // or today), arrows move within.
              tabIndex={i === (scrub ?? days.length - 1) ? 0 : -1}
              onFocus={() => setScrub(i)}
              onClick={() => onPickDay(d.day)}
              aria-label={`Open ${describe(d)}`}
            />
          );
        })}
      </div>
      <div className={classes.activityAxis} aria-hidden>
        <span>{days[0].day}</span>
        <span>{days[days.length - 1].day}</span>
      </div>
    </Box>
  );
};

/**
 * Downloads text as a file without leaving the page.
 *
 * The anchor is never attached to the document. Appending it put server-derived data (the
 * filename carries the week and the active scope) into `document.body.appendChild`, which
 * Snyk Code reports as DOM-based XSS. It is not exploitable here: `href` and `download` are
 * set as properties so nothing is parsed as HTML, and exportFilename already reduces the name
 * to `[A-Za-z0-9._-]`. But a detached click needs no such argument, and "safe because of a
 * sanitiser two modules away" is a worse thing to have to keep proving than "never in the
 * DOM".
 *
 * A detached anchor is clickable in every browser this portal supports; the export is
 * exercised end to end in the browser rather than assumed.
 */
function download(filename: string, body: string) {
  const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  try {
    a.click();
  } finally {
    // Deferred, not immediate. Chromium starts the download synchronously inside click(), so
    // revoking straight after works there -- and that is the only engine this was verified
    // in. Firefox has historically cancelled a detached-anchor download when the URL is
    // revoked in the same task. A macrotask costs nothing and removes the engine dependency;
    // `finally` still guarantees the blob is released even if click() throws.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

const actorish = (dim: Dimension) => dim === 'author' || dim === 'trigger';

const displayValue = (node: GroupNode) => {
  if (actorish(node.dim) && node.value === UNKNOWN_ACTOR) return 'unattributed';
  // The value arrives QUALIFIED (org/repo#n) from the rollup, so no prefixing here.
  if (node.dim === 'pr') return node.value === UNKNOWN_ACTOR ? 'no PR' : node.value;
  if (node.dim === 'savingsMethod' && node.value === UNKNOWN_ACTOR) {
    return 'no savings reported';
  }
  return node.value;
};

/**
 * View-state <-> URL. The address bar names the view: period, timezone,
 * grouping, narrowing chips, and open drills travel as query params, so a view
 * can be shared, bookmarked, and walked back through with Back/Forward. Only
 * non-default state is written -- a pristine view keeps a clean URL. Anything
 * unparseable in a pasted URL falls back to the default rather than guessing.
 */
type UrlViewState = {
  period?: Period;
  tz?: string;
  dims?: Dimension[];
  focus?: Focus[];
  drills?: Set<string>;
};

function parseViewState(search: string): UrlViewState {
  const q = new URLSearchParams(search);
  const out: UrlViewState = {};
  const p = q.get('p');
  if (p) {
    const w = /^w(\d{1,3})$/.exec(p);
    const d = /^d(\d{4}-\d{2}-\d{2})$/.exec(p);
    const m = /^m(\d{4}-\d{2})$/.exec(p);
    if (w) out.period = { granularity: 'week', offset: Number(w[1]) };
    else if (d) {
      // Shape is not enough: d2026-99-99 must fall back, not reach the server.
      const t = new Date(`${d[1]}T00:00:00Z`);
      if (!Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === d[1]) {
        out.period = { granularity: 'day', day: d[1] };
      }
    } else if (m) {
      const month = Number(m[1].slice(5));
      if (month >= 1 && month <= 12) out.period = { granularity: 'month', month: m[1] };
    }
  }
  const tz = q.get('tz');
  if (tz) {
    try {
      // Validates the zone name; an invalid one throws and is ignored.
      new Intl.DateTimeFormat('en-US', { timeZone: tz });
      out.tz = tz;
    } catch {
      /* fall back below */
    }
  }
  const dims = q.get('dims');
  if (dims) {
    const valid = dims
      .split(',')
      .filter((v): v is Dimension => (DAY_DIMENSIONS as readonly string[]).includes(v));
    // The pickers cap grouping at two levels and forbid the model x
    // savings-method combination (savings cells carry no model); a pasted URL
    // must not restore a state the controls themselves cannot reach.
    const deconflicted = [...new Set(valid)].slice(0, 2);
    if (deconflicted.includes('model') && deconflicted.includes('savingsMethod')) {
      deconflicted.length = 1;
    }
    if (deconflicted.length) out.dims = deconflicted;
  }
  const focus = q.getAll('f').flatMap((raw): Focus[] => {
    const i = raw.indexOf(':');
    if (i <= 0) return [];
    const dim = raw.slice(0, i);
    const value = raw.slice(i + 1);
    // savingsMethod chips are refused at the source (addFocus): token rows
    // carry no method, so the chip would silently filter honest figures away.
    return (DAY_DIMENSIONS as readonly string[]).includes(dim) &&
      dim !== 'savingsMethod' &&
      value
      ? [{ dim: dim as Dimension, value }]
      : [];
  });
  if (focus.length) out.focus = focus;
  const drills = q.getAll('drill');
  if (drills.length) out.drills = new Set(drills);
  // A URL that pins ANY view state pins UTC unless it names a zone — a shared
  // link must show the receiver the sharer's boundaries, not the receiver's
  // stored preference. A pristine URL keeps resolving to the stored default.
  if (!out.tz && (out.period || out.dims || out.focus || out.drills)) {
    out.tz = 'UTC';
  }
  return out;
}

function serializeViewState(state: {
  period: Period;
  tz: string;
  dims: Dimension[];
  focus: Focus[];
  drills: ReadonlySet<string>;
}): string {
  const q = new URLSearchParams();
  const { period, tz, dims, focus, drills } = state;
  if (period.granularity === 'week') {
    if (period.offset !== 0) q.set('p', `w${period.offset}`);
  } else if (period.granularity === 'day') {
    q.set('p', `d${period.day}`);
  } else {
    q.set('p', `m${period.month}`);
  }
  if (dims.join(',') !== 'org,repo') q.set('dims', dims.join(','));
  for (const f of focus) q.append('f', `${f.dim}:${f.value}`);
  for (const d of [...drills].sort()) q.append('drill', d);
  // A zone is pinned whenever the URL pins anything else (the mirror of
  // parseViewState's absent-tz-means-UTC rule), or whenever it differs from
  // the UTC default -- so sharer and receiver always cut the same boundaries.
  if (tz !== 'UTC' || [...q.keys()].length > 0) q.set('tz', tz);
  return q.toString();
}

export const ClaudeMetricsPanel = () => {
  const classes = useStyles();
  // State a shared link or bookmark carried in; read once at mount.
  const [urlState] = useState<UrlViewState>(() => parseViewState(window.location.search));
  const [period, setPeriod] = useState<Period>(
    urlState.period ?? { granularity: 'week', offset: 0 },
  );
  const [knownMax, setKnownMax] = useState<number>();
  // Org then Repo: the landing question is "what did the fleet spend, and
  // where" — org totals first, repos under them (owner-requested default).
  const [dims, setDims] = useState<Dimension[]>(urlState.dims ?? ['org', 'repo']);
  const [focus, setFocus] = useState<Focus[]>(urlState.focus ?? []);
  // Bumping this refetches everything the panel shows. Counter, not boolean: two
  // clicks in a row must both fire.
  const [reload, setReload] = useState(0);
  const [sort, setSort] = useState<Sort>({ key: 'total', dir: 'desc' });
  // Text filter over the RENDERED tree -- the in-panel Ctrl-F. Display pruning
  // only; the aggregation grammar (chips) stays the tool for changing numbers.
  const [rowFilter, setRowFilter] = useState('');
  // Collapsed group paths (top-level only). Per-session by design.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  // Which group rows have their finding-level drill-down open (keyed by path).
  const [reviewOpen, setReviewOpen] = useState<Set<string>>(
    () => urlState.drills ?? new Set(),
  );
  // The zone day and month boundaries are cut in. Defaults to UTC so the panel
  // agrees with the Console's own bucketing; a reader who wants local days can
  // override it, and the choice persists per browser.
  const [tz, setTz] = useState<string>(() => urlState.tz ?? loadTz());
  const [tzOpen, setTzOpen] = useState(false);

  // Keep the address bar naming the view. A period or grouping change is
  // NAVIGATION (pushState, so Back returns to the previous view); chips,
  // drills, and timezone refine the view in place (replaceState). Both writes
  // are fenced to the route the panel mounted on, carry any foreign params
  // (utm and friends) through untouched, and keep the fragment.
  const mountPathRef = useRef(window.location.pathname);
  const lastNavRef = useRef({ period, dims });
  useEffect(() => {
    if (window.location.pathname !== mountPathRef.current) return;
    const ours = new URLSearchParams(
      serializeViewState({ period, tz, dims, focus, drills: reviewOpen }),
    );
    const merged = new URLSearchParams(window.location.search);
    for (const key of ['p', 'tz', 'dims', 'f', 'drill']) merged.delete(key);
    for (const [key, value] of ours) merged.append(key, value);
    const next = merged.toString();
    const current = window.location.search.replace(/^\?/, '');
    if (next !== current) {
      const url = `${window.location.pathname}${next ? `?${next}` : ''}${
        window.location.hash
      }`;
      const isNav =
        JSON.stringify(lastNavRef.current.period) !== JSON.stringify(period) ||
        lastNavRef.current.dims.join(',') !== dims.join(',');
      if (isNav) window.history.pushState(null, '', url);
      else window.history.replaceState(null, '', url);
    }
    lastNavRef.current = { period, dims };
  }, [period, tz, dims, focus, reviewOpen]);

  // Back/Forward re-apply the URL onto the state; after the states settle the
  // sync effect above serializes to exactly the popped search and writes
  // nothing, so no loop. Fenced to the mount route: a Back that leaves the
  // page must not decode the destination's URL as view state.
  useEffect(() => {
    const onPop = () => {
      if (window.location.pathname !== mountPathRef.current) return;
      const restored = parseViewState(window.location.search);
      setPeriod(restored.period ?? { granularity: 'week', offset: 0 });
      setTz(restored.tz ?? loadTz());
      setDims(restored.dims ?? ['org', 'repo']);
      setFocus(restored.focus ?? []);
      setReviewOpen(restored.drills ?? new Set());
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const { data, loading, error, owners, ownersFailed, previousRows } =
    useClaudeMetrics(period, undefined, reload, tz);
  // The trust chart's data: independent of the selected period, refreshed by
  // the same nonce so the Refresh button covers it too.
  const { days: activityDays } = useClaudeActivity(reload, tz);
  // The finding grain behind the Breakdown's review-quality columns.
  const { data: reviewData } = useClaudeReviewQuality(reload);

  const week = data?.granularity === 'week' ? data : undefined;
  const dayData = data?.granularity === 'day' ? data : undefined;
  const monthData = data?.granularity === 'month' ? data : undefined;
  const offset = period.granularity === 'week' ? period.offset : 0;

  // True while the in-flight fetch is a BACKGROUND poll (the 60s auto-refresh).
  // Background updates skip the stale presentation entirely -- the numbers just
  // roll to their new values when they land -- so the dim/settle treatment stays
  // a signal for explicit actions. Reset when the fetch completes, so a leftover
  // true can never suppress the dim on a subsequent period change or refresh.
  const pollRef = useRef(false);

  // While the next read is in flight the previous answer stays mounted, dimmed
  // (the hook keeps it -- stale-while-revalidate). A granularity mismatch
  // additionally blanks the period label: a week title over a selected Day
  // toggle would be a lie, dimmed or not.
  const reloadingStale = loading && data !== undefined && !pollRef.current;
  const granularityMismatch =
    loading && data !== undefined && data.granularity !== period.granularity;

  // Bumped each time a USER-INITIATED fetch lands, so the settle animation plays
  // exactly when fresh data arrives -- not when the request starts, and not on
  // background polls (the remount would reset table scroll mid-read every
  // minute). Client-side regroups (dims/sort/focus) join it in viewKey below.
  const [settleStamp, setSettleStamp] = useState(0);
  const wasLoading = useRef(false);
  useEffect(() => {
    if (wasLoading.current && !loading) {
      if (!pollRef.current) setSettleStamp(n => n + 1);
      pollRef.current = false;
    }
    wasLoading.current = loading;
  }, [loading]);

  // Auto-refresh: metrics land server-side seconds after a run ends (the consumer
  // rolls the week up on ingest), so the panel polls quietly every minute while
  // the tab is visible instead of making the analyst click Refresh. Paused during
  // a fetch and under an error -- silent retry flapping the error panel every
  // minute would be worse than staying still.
  useEffect(() => {
    if (loading || error) return undefined;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      pollRef.current = true;
      setReload(n => n + 1);
    }, 60_000);
    return () => window.clearInterval(id);
  }, [loading, error]);

  useEffect(() => {
    if (week) setKnownMax(week.maxOffset);
  }, [week]);

  const maxOffset = effectiveMaxOffset(week?.maxOffset, knownMax, offset);

  // Day and month are LOCAL calendar periods: the viewer's zone decides where
  // they begin, what "today" means, and what the header calls them. Weeks stay
  // ISO weeks in UTC -- the persisted rollup grain -- so weekly jump math below
  // keeps using utcToday.
  const today = todayIn(tz);
  const thisMonth = thisMonthIn(tz);

  // Raw-backed navigation floor: the oldest raw row. Earlier days and months hold
  // no data at all (raw rows are pruned), so navigation stops where the data does.
  const dayFloor = dayData?.oldestDay ?? monthData?.oldestDay;
  const monthFloor = dayFloor ? monthOfDay(dayFloor) : undefined;

  // A month whose early days hold no data -- eaten by the prune for a past
  // month, or simply older than the data set for the current one. Said, not
  // implied: a truncated month rendering as a small quiet one would be the
  // exact silent under-report this panel exists to prevent.
  const monthPartialFrom =
    monthData &&
    monthData.oldestDay &&
    monthOfDay(monthData.oldestDay) === monthData.month &&
    monthData.oldestDay !== `${monthData.month}-01`
      ? monthData.oldestDay
      : undefined;

  /**
   * The date picker filters EVERY granularity: in Day view it shows that day, in
   * Month view the month containing it, and in Week view it jumps to the week
   * containing the date (clamped to the retention edge rather than letting the
   * server 400 a request the client could refuse).
   */
  const switchGranularity = (g: 'week' | 'day' | 'month') => {
    if (g === period.granularity) return;
    setPeriod(
      g === 'day'
        ? { granularity: 'day', day: todayIn(tz) }
        : g === 'month'
        ? { granularity: 'month', month: thisMonthIn(tz) }
        : { granularity: 'week', offset: 0 },
    );
    // No dimension stripping on any switch: every granularity is a raw-backed
    // read-time rollup now, so PR groupings and chips survive week/day/month
    // moves intact.
  };

  /**
   * Change the zone day and month boundaries are cut in.
   *
   * Re-anchors the selected period so "today" stays today: late-evening CDT is
   * already the next UTC day, so a switch without this leaves a reader looking at
   * yesterday. A deliberately-chosen PAST date is left alone -- moving it would
   * change which data they are reading, which is not what picking a zone asked for.
   */
  const changeTz = (next: string) => {
    if (next === tz) return;
    if (period.granularity === 'day') {
      setPeriod({ granularity: 'day', day: reanchorDay(period.day, tz, next) });
    } else if (period.granularity === 'month') {
      setPeriod({
        granularity: 'month',
        month: reanchorMonth(period.month, tz, next),
      });
    }
    saveTz(next);
    setTz(next);
  };

  /** UTC and the reader's own zone earn a suffix; everything else is just its name. */
  const tzOptionLabel = (z: string) =>
    z === ANTHROPIC_TZ
      ? `${z} (Anthropic)`
      : z === browserTz()
      ? `${z} (local)`
      : z;

  const jumpToDate = (day: string) => {
    if (!day) return;
    setPeriod(p =>
      p.granularity === 'day'
        ? { granularity: 'day', day }
        : p.granularity === 'month'
        ? { granularity: 'month', month: monthOfDay(day) }
        : // utcToday, not the zone-local today: the server resolves offset 0
          // against UTC "now", so a local anchor is off by one ISO week for the
          // hours where the two calendars disagree on what day it is.
          {
            granularity: 'week',
            offset: Math.min(maxOffset, weeksBack(utcToday(), day)),
          },
    );
  };

  // Narrowing happens client-side because the response carries the full grain: instant,
  // composable across every dimension, and it cannot disagree with what is on screen.
  // savingsMethod never appears here (addFocus refuses it -- token rows carry no
  // method), and skipping it defensively beats filtering every row to nothing.
  const applyFocus = (rows: readonly WeeklyRow[]) =>
    focus.reduce<readonly WeeklyRow[]>((acc, f) => {
      const { dim } = f;
      if (dim === 'savingsMethod') return acc;
      return acc.filter(r => r[dim] === f.value);
    }, rows);

  // Team is enriched CLIENT-SIDE before anything narrows or groups: it is a
  // projection of repo through the catalog map, and the buckets are honest --
  // 'lookup failed' when the catalog call failed, 'not in catalog' when it
  // succeeded and the repo has no owner.
  const enrich = (rs: readonly WeeklyRow[]): WeeklyRow[] =>
    rs.map(r => ({
      ...r,
      team: ownersFailed
        ? 'lookup failed'
        : displayOwner(owners.get(`${r.org}/${r.repo}`.toLowerCase())) ??
          'not in catalog',
    }));

  const allRows = useMemo(
    () => (data ? enrich(data.rows) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, owners, ownersFailed],
  );
  const rows = useMemo(
    () => applyFocus(allRows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRows, focus],
  );
  // Run-grain records for exact run counting at model-spanning groups: same
  // team projection and the same narrowing as the rows. Omitted entirely under
  // a model narrowing -- every group is then single-model and the rows count
  // exactly on their own.
  const countingRuns = useMemo(() => {
    if (!data || focus.some(f => f.dim === 'model')) return undefined;
    const enriched = data.runCosts.map(c => ({
      ...c,
      team: ownersFailed
        ? 'lookup failed'
        : displayOwner(owners.get(`${c.org}/${c.repo}`.toLowerCase())) ??
          'not in catalog',
    }));
    return focus.reduce<readonly (typeof enriched)[number][]>(
      (acc, f) =>
        acc.filter(c => {
          const v =
            f.dim === 'pr'
              ? c.pr
              : (c as Record<string, unknown>)[f.dim];
          return v === f.value;
        }),
      enriched,
    );
  }, [data, focus, owners, ownersFailed]);

  // The run-grain savings cells: same team projection and the same narrowing
  // as the count records, and the same model rule (cells carry no model).
  const savingsCells = useMemo(() => {
    if (!data || focus.some(f => f.dim === 'model')) return undefined;
    // ?? []: a server one deploy behind ships no runSavings field at all.
    const enriched = (data.runSavings ?? []).map(c => ({
      ...c,
      team: ownersFailed
        ? 'lookup failed'
        : displayOwner(owners.get(`${c.org}/${c.repo}`.toLowerCase())) ??
          'not in catalog',
    }));
    return focus.reduce<readonly (typeof enriched)[number][]>(
      (acc, f) =>
        acc.filter(c => {
          const v =
            f.dim === 'pr'
              ? c.pr
              : (c as Record<string, unknown>)[f.dim];
          return v === f.value;
        }),
      enriched,
    );
  }, [data, focus, owners, ownersFailed]);

  // Review-quality findings (PLAT-1819): the same team projection and the
  // same narrowing as the rows, plus period scoping — a finding belongs to
  // the displayed period when its last review activity landed in it.
  const reviewScope = useMemo<PeriodScope | undefined>(() => {
    if (!data) return undefined;
    if (data.granularity === 'week') {
      return { granularity: 'week', isoYear: data.isoYear, isoWeek: data.isoWeek };
    }
    if (data.granularity === 'day') return { granularity: 'day', day: data.day, tz };
    return { granularity: 'month', month: data.month, tz };
  }, [data, tz]);

  // A tree-wide withhold reason beats every per-node join: a truncated
  // findings payload, a period the findings window does not fully cover, or a
  // narrowing chip on a dimension findings cannot be attributed to.
  const reviewWithhold = useMemo(() => {
    if (!reviewData || !reviewScope) return undefined;
    if (reviewData.truncated) return 'truncated' as const;
    if (!windowCoversPeriod(reviewScope, reviewData.since)) return 'window' as const;
    return focus.find(f => !REVIEW_JOINABLE.has(f.dim))?.dim;
  }, [reviewData, reviewScope, focus]);

  const reviewFindings = useMemo<TeamedFinding[]>(() => {
    if (!reviewData || !reviewScope || reviewWithhold) return [];
    const inPeriod = makePeriodFilter(reviewScope);
    const teamed = reviewData.findings.filter(inPeriod).map(f => ({
      ...f,
      team: ownersFailed
        ? 'lookup failed'
        : displayOwner(owners.get(`${f.org}/${f.repo}`.toLowerCase())) ??
          'not in catalog',
    }));
    return focus.reduce<TeamedFinding[]>(
      (acc, f) => acc.filter(x => matchesConstraint(x, f)),
      teamed,
    );
  }, [reviewData, reviewScope, reviewWithhold, focus, owners, ownersFailed]);

  const groups = useMemo(() => {
    const built = groupRows(rows, dims, sort, countingRuns, savingsCells);
    // Attach only when a findings payload exists: cells stay ABSENT (not
    // withheld) on a server that predates the route, and the token tree never
    // waits on the findings fetch.
    if (reviewData) {
      attachReviewQuality(built, reviewFindings, reviewWithhold);
      // groupRows sorted before the review cells existed, so a review-backed
      // key must sort again — otherwise Findings/Fix rate order alphabetically.
      if (sort.key === 'findings' || sort.key === 'fixRate') resortTree(built, sort);
    }
    return built;
  }, [rows, dims, sort, countingRuns, savingsCells, reviewData, reviewFindings, reviewWithhold]);
  // What actually renders: the group tree pruned to the filter. A match keeps
  // its subtree; a matching descendant keeps its ancestors as the path to it.
  const visible = useMemo(
    () => filterGroups(groups, rowFilter, displayValue),
    [groups, rowFilter],
  );

  const deltas = useMemo(
    () =>
      previousRows
        ? deltasByPath(groups, groupRows(applyFocus(enrich(previousRows)), dims, sort))
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, previousRows, dims, focus, sort, owners, ownersFailed],
  );

  // From the narrowed ROWS, not a sum of top-level group totals: identical for
  // every partitioning dimension, and the only correct answer under a
  // savings-method grouping, whose nodes deliberately carry zeroed totals
  // (methods overlap on runs). Summing those zeroed the whole band -- "total
  // tokens 0" and a "-100%" delta on a period that has data, the confidently
  // wrong number this surface exists to refuse.
  const grand: Totals | undefined = groups.length ? totals(rows) : undefined;

  // Period-over-period on the grand total: the same comparison the rows carry, at the
  // top, because "did we spike" is asked before "where".
  const weekDelta = useMemo(() => {
    if (!grand || !previousRows) return undefined;
    const before = applyFocus(previousRows).reduce(
      (n, r) =>
        n + r.inputTokens + r.outputTokens + r.cacheWriteTokens + r.cacheReadTokens,
      0,
    );
    if (!before) return undefined;
    const pct = ((grand.totalTokens - before) / before) * 100;
    // The baseline travels WITH the percentage: "+859%" alone is technically exact
    // and practically alarming when last period barely existed -- "vs 3.92M" lets
    // the number defuse itself.
    return {
      text: `${pct > 0 ? '+' : ''}${pct.toFixed(Math.abs(pct) >= 10 ? 0 : 1)}%`,
      up: pct > 0,
      baseline: before,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grand, previousRows, focus]);

  // Reported cost: the action's own metering, per run. Exact under every narrowing
  // except model, which splits runs -- then it is withheld, not approximated.
  const grandReported = useMemo(
    () => (data ? reportedCost(data.runCosts, focus) : undefined),
    [data, focus],
  );

  // Reported savings under the same narrowing, with the consolidated per-method
  // breakdown for the strip under the tiles.
  const grandSavings = useMemo(
    () =>
      data ? reportedSavings(data.runSavings ?? [], data.runCosts, focus) : undefined,
    [data, focus],
  );
  // The UN-narrowed savings, for the withheld tile's "does anything exist to
  // withhold" test — memoized so the JSX condition doesn't re-scan the cells
  // twice per render.
  const allSavings = useMemo(
    () => (data ? reportedSavings(data.runSavings ?? [], data.runCosts, []) : undefined),
    [data],
  );
  // The debounce's yield under the same narrowing; the un-narrowed read backs
  // the withheld tile's "does anything exist to withhold" test, like allSavings.
  const grandSuperseded = useMemo(
    () => (data ? supersededShare(data.runCosts, focus) : undefined),
    [data, focus],
  );
  const allSuperseded = useMemo(
    () => (data ? supersededShare(data.runCosts, []) : undefined),
    [data],
  );

  const quiet = data ? silences(data.lastReceived, new Date()) : [];
  const reportingStatus = data ? statusHeadline(quiet) : '';

  const derivation = data
    ? describeDerivation({
        period:
          data.granularity === 'week'
            ? { kind: 'week', isoYear: data.isoYear, isoWeek: data.isoWeek }
            : data.granularity === 'day'
            ? {
                kind: 'day',
                day: data.day,
                retentionWeeks: data.retentionWeeks,
                tz: data.tz,
              }
            : {
                kind: 'month',
                month: data.month,
                retentionWeeks: data.retentionWeeks,
                tz: data.tz,
              },
        rows,
        // The week's rows BEFORE view narrowing: which repos went silent is a fact
        // about the WEEK. Without this, one click on any chip accused every filtered-out
        // repo of silence. (An earlier 'fix' of this line was an unasserted replace that
        // silently no-opped on an indentation mismatch -- hence the wiring test in
        // claudeMetricsPanel.test.tsx, which fails if this prop is ever dropped again.)
        allRows,
        groups,
        dims,
        filter: data.actorFilter,
        focus,
        knownRepos: data.lastReceived,
      })
    : undefined;

  /** A dimension used twice would create a level that cannot split anything. */
  const dedupe = (dims_: Dimension[]) => dims_.filter((d, i) => dims_.indexOf(d) === i);

  // Every granularity is a raw-backed read-time rollup, so PR is offered
  // everywhere. (The persisted weekly table still has no pr column -- it is the
  // archive, not the serving path.)
  const availableDims = DAY_DIMENSIONS;

  // Split by level rather than one shared `Dimension | 'none'` parameter. Level 0 renders no
  // "nothing" option, so 'none' can never arrive there -- but a shared signature said it
  // could, and the `as Dimension` that silenced the compiler would have written the literal
  // string 'none' into dims[0]. `build()` then reads r['none'], every row yields undefined,
  // and the table renders one group labelled `undefined` holding the entire week's spend.
  // Unrepresentable beats asserted-away.
  const setPrimary = (value: Dimension) => {
    setDims(current => {
      // Model and Savings method cannot compose (savings cells carry no model):
      // picking one as primary DROPS a conflicting secondary rather than being
      // unpickable -- the selection states the intent, the conflict resolves.
      const conflict =
        (value === 'savingsMethod' && current[1] === 'model') ||
        (value === 'model' && current[1] === 'savingsMethod');
      return conflict ? [value] : dedupe([value, ...current.slice(1)]);
    });
    // Drill paths are keyed by the grouping's values; under a new grouping the
    // old ones are dead entries that would otherwise linger in shared URLs.
    setReviewOpen(new Set());
  };

  const setSecondary = (value: Dimension | 'none') => {
    setDims(current => (value === 'none' ? [current[0]] : dedupe([current[0], value])));
    setReviewOpen(new Set());
  };

  const addFocus = (node: GroupNode) => {
    // A savings method cannot narrow the token rows -- they carry no method, so
    // "narrow to sqlite-context" would filter the table to nothing while looking
    // like a working chip. The method grouping IS that narrowing.
    if (node.dim === 'savingsMethod') return;
    setFocus(current =>
      current.some(f => f.dim === node.dim && f.value === node.value)
        ? current
        : [...current, { dim: node.dim, value: node.value }],
    );
  };

  const exportCtx = derivation && {
    generatedAt: new Date(),
    derivation,
    // The shown-view export mirrors the screen, so the pruning must be stamped.
    rowFilter: rowFilter.trim() || undefined,
  };

  const toggleSort = (key: SortKey) =>
    setSort(s =>
      s.key === key
        ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' },
    );

  /** A sortable column header: real button, aria-sort on the live column. */
  const SortHeader = ({ label, k, title }: { label: string; k: SortKey; title?: string }) => {
    const active = sort.key === k;
    const inner = (
      <button
        type="button"
        className={classes.sortButton}
        onClick={() => toggleSort(k)}
        aria-label={`Sort by ${label}${active && sort.dir === 'desc' ? ', ascending next' : ', descending next'}`}
      >
        {label}
        {active && (
          <span aria-hidden className={classes.sortArrow}>
            {sort.dir === 'desc' ? '▼' : '▲'}
          </span>
        )}
      </button>
    );
    return (
      <TableCell
        className={classes.num}
        aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : undefined}
      >
        {title ? (
          <Tooltip title={title}>
            <span tabIndex={0}>{inner}</span>
          </Tooltip>
        ) : (
          inner
        )}
      </TableCell>
    );
  };

  const toggleCollapse = (path: string) =>
    setCollapsed(current => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const grandVisibleTotal = grand?.totalTokens ?? 0;

  // The table's identity: remounting on this key replays the settle animation for
  // every way the view below the rail can change -- fresh data landing
  // (settleStamp) or an instant client-side regroup (dims / sort / focus).
  const viewKey = [
    settleStamp,
    dims.join('.'),
    `${sort.key}:${sort.dir}`,
    focus.map(f => `${f.dim}=${f.value}`).join(','),
  ].join('|');

  /** The same team projection the rows get, for the drill's client-side filter. */
  const teamOf = (org: string, repo: string): string =>
    ownersFailed
      ? 'lookup failed'
      : displayOwner(owners.get(`${org}/${repo}`.toLowerCase())) ?? 'not in catalog';

  /**
   * Server filters for the run drill: the row's own constraint chain plus the
   * active narrowing chips. pr-review runs only, unless a Run-type constraint
   * names another job explicitly. team stays client-side (see teamOf).
   */
  const runFiltersFor = (constraints: ReviewConstraint[]): RunQueryFilters => {
    const filters: RunQueryFilters = {};
    for (const c of [...focus, ...constraints]) {
      if (c.dim === 'org') filters.org = c.value;
      else if (c.dim === 'repo') filters.repo = c.value;
      else if (c.dim === 'author') filters.author = c.value;
      else if (c.dim === 'trigger') filters.trigger = c.value;
      else if (c.dim === 'model') filters.model = c.value;
      else if (c.dim === 'job') filters.job = c.value;
      else if (c.dim === 'pr' && c.value !== UNKNOWN_ACTOR) {
        const [slug, pr] = c.value.split('#');
        const [org, repo] = slug.split('/');
        filters.org = org;
        filters.repo = repo;
        filters.pr = Number(pr);
      }
    }
    if (filters.job === undefined) filters.job = REVIEW_JOB;
    return filters;
  };

  const toggleReview = (path: string) =>
    setReviewOpen(current => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  // The reason travels on EVERY withheld cell, not only the header: a reader
  // deep in the table should never need to scroll up to learn why a dash.
  const withheldTokensDash = (
    <Tooltip title="Withheld: savings-method groups overlap on runs (a run can report several methods), so per-run figures cannot be attributed to one method without double-counting.">
      <span tabIndex={0} className={classes.quiet}>—</span>
    </Tooltip>
  );

  /** A withheld or unavailable review cell, with its reason on hover/focus. */
  const reviewDash = (node: GroupNode) => (
    <Tooltip
      title={
        node.reviewWithheldBy
          ? reviewWithheldReason(node.reviewWithheldBy)
          : 'Review-quality data unavailable — no findings payload arrived (a server one deploy behind has none).'
      }
    >
      <span tabIndex={0} className={classes.quiet}>—</span>
    </Tooltip>
  );

  const renderRow = (node: GroupNode, depth: number, ancestors: ReviewConstraint[]) => {
    const delta = deltas?.get(node.path);
    const constraints: ReviewConstraint[] = [
      ...ancestors,
      { dim: node.dim, value: node.value },
    ];
    const canDrill =
      node.children.length === 0 &&
      node.dim !== 'savingsMethod' &&
      node.hasReviewRuns === true &&
      !(node.dim === 'pr' && node.value === UNKNOWN_ACTOR) &&
      !constraints.some(c => c.dim === 'savingsMethod') &&
      !focus.some(f => f.dim === 'savingsMethod');
    const drillOpen = canDrill && reviewOpen.has(node.path);
    const label = formatDelta(delta);
    const share = grandVisibleTotal > 0 ? node.totals.totalTokens / grandVisibleTotal : 0;
    // While the filter is active every path to a match must be visible, so
    // collapse is bypassed rather than fought fold by fold.
    const isCollapsed = !rowFilter.trim() && collapsed.has(node.path);
    return (
      <React.Fragment key={node.path}>
        <TableRow className={depth === 0 ? classes.groupStart : undefined}>
          <TableCell
            className={`${classes.labelCell} ${depth > 0 ? classes.labelDeep : ''}`}
            style={{ paddingLeft: depth * 24 + 16 }}
          >
            {depth === 0 && node.children.length > 0 && (
              <button
                type="button"
                className={classes.collapseBtn}
                onClick={() => toggleCollapse(node.path)}
                aria-expanded={!isCollapsed}
                aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${displayValue(node)}`}
              >
                {isCollapsed ? <ChevronRightSmallIcon /> : <ExpandMoreIcon />}
              </button>
            )}
            {canDrill && (
              <button
                type="button"
                className={classes.collapseBtn}
                onClick={() => toggleReview(node.path)}
                aria-expanded={drillOpen}
                aria-label={`${drillOpen ? 'Hide' : 'Show'} the pr-review runs behind ${constraints
                  .map(c => (c.value === UNKNOWN_ACTOR ? 'unattributed' : c.value))
                  .join(' / ')}`}
              >
                {drillOpen ? <ExpandMoreIcon /> : <ChevronRightSmallIcon />}
              </button>
            )}
            {node.dim === 'savingsMethod' ? (
              /* A method group is not narrowable (token rows carry no method),
                 so its label is not a button pretending otherwise — it is the
                 glossary surface: what this savings method means, on hover
                 and on keyboard focus. */
              <Tooltip
                title={
                  node.value === UNKNOWN_ACTOR
                    ? 'These runs reported no savings method.'
                    : describeSavingsMethod(node.value)
                }
              >
                <span tabIndex={0} className={classes.focusable}>
                  {displayValue(node)}
                </span>
              </Tooltip>
            ) : (
              <button
                type="button"
                className={classes.focusable}
                onClick={() => addFocus(node)}
                aria-label={`Narrow to ${DIMENSION_LABELS[node.dim]} ${displayValue(node)}`}
              >
                {displayValue(node)}
              </button>
            )}
            {/* The value narrows (the panel's grammar); the icon leaves for GitHub.
                Qualified values are org/repo#n, so the URL derives directly. */}
            {node.dim === 'pr' && node.value !== UNKNOWN_ACTOR && (
              <Link
                className={classes.prLink}
                href={githubUrl(...node.value.replace('#', '/pull/').split('/'))}
                target="_blank"
                rel="noopener"
                aria-label={`Open ${node.value} on GitHub`}
              >
                <OpenInNewIcon />
              </Link>
            )}
          </TableCell>
          <TableCell className={classes.teamCell}>
            {node.dim === 'repo' ? <OwnerCell node={node} owners={owners} failed={ownersFailed} /> : null}
          </TableCell>
          <TableCell className={`${classes.num} ${classes.barCell}`}>
            {node.tokensWithheldBy ? (
              <Tooltip title="Withheld: savings-method groups overlap on runs (a run can report several methods), so token figures cannot be attributed to one method without double-counting.">
                <span tabIndex={0} className={classes.quiet}>—</span>
              </Tooltip>
            ) : (
              <>
                {/* The bar encodes share-of-visible-total; the number stays the record. */}
                <span
                  aria-hidden
                  className={classes.magnitudeBar}
                  style={{ width: `${Math.min(100, share * 100).toFixed(2)}%` }}
                />
                <span className={classes.barValue}>
                  {formatTokens(node.totals.totalTokens)}
                  {depth === 0 && share > 0 && (
                    <span className={classes.share}>{Math.round(share * 100)}%</span>
                  )}
                </span>
              </>
            )}
          </TableCell>
          <TableCell className={classes.num}>
            {node.tokensWithheldBy ? (
              <Tooltip title="Withheld: a run's cost cannot be attributed to one savings method — method groups overlap on runs.">
                <span tabIndex={0} className={classes.quiet}>—</span>
              </Tooltip>
            ) : node.reportedCostUsd !== undefined ? (
              node.costedRuns !== undefined &&
              node.totalRuns !== undefined &&
              node.costedRuns < node.totalRuns ? (
                <Tooltip
                  title={`Partial coverage: ${node.costedRuns} of ${node.totalRuns} runs reported a cost (the rest predate the cost field).`}
                >
                  <span tabIndex={0}>{formatUsd(node.reportedCostUsd)}*</span>
                </Tooltip>
              ) : (
                formatUsd(node.reportedCostUsd)
              )
            ) : node.totalRuns !== undefined ? (
              <Tooltip
                title={`No cost data, not free: none of this group's ${node.totalRuns} run${node.totalRuns === 1 ? '' : 's'} reported a cost (they predate the cost field).`}
              >
                <span tabIndex={0} className={classes.quiet}>—</span>
              </Tooltip>
            ) : (
              <Tooltip title="Withheld, not free: a run reports one cost with no per-model split, so a group that splits runs by model has no honest figure. Regroup without Model for exact costs.">
                <span tabIndex={0} className={classes.quiet}>—</span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell className={classes.num}>
            {node.savingsUsd !== undefined && node.coverage ? (
              <Tooltip title="Coverage, not savings: this row reports how many runs the method reached, not dollars. It is deliberately unpriced — it fires on every review, including the many that would never have run a query, so pricing each one as an avoided query would invent savings that never happened.">
                <span tabIndex={0} className={classes.quiet}>
                  covered
                </span>
              </Tooltip>
            ) : node.savingsUsd !== undefined ? (
              node.savedRuns !== undefined &&
              node.totalRuns !== undefined &&
              node.savedRuns < node.totalRuns ? (
                <Tooltip
                  title={
                    // The "counted" phrasing is only true in the regular
                    // tree. Inside a method subtree the cells are reduced to
                    // ONE method — including the ambient prompt-cache
                    // drill-down, whose runs are precisely NOT counted — so
                    // its coverage sentence stays method-generic.
                    node.tokensWithheldBy === 'savingsMethod'
                      ? `Reported by ${node.savedRuns} of ${node.totalRuns} runs — this method's own run coverage.`
                      : `Reported by ${node.savedRuns} of ${node.totalRuns} runs — only runs that report a counted savings method carry any.`
                  }
                >
                  <span tabIndex={0}>{formatUsd(node.savingsUsd)}*</span>
                </Tooltip>
              ) : (
                formatUsd(node.savingsUsd)
              )
            ) : node.dim === 'savingsMethod' && node.value === UNKNOWN_ACTOR ? (
              <Tooltip title="These runs reported no savings method.">
                <span tabIndex={0} className={classes.quiet}>—</span>
              </Tooltip>
            ) : (
              <Tooltip title="No counted savings method in this group — absence of an attributable report, not a measured zero; any cache discount sits beside the cache-read total.">
                <span tabIndex={0} className={classes.quiet}>—</span>
              </Tooltip>
            )}
          </TableCell>
          {/* Review-quality columns (PLAT-1819): the finding grain joined into
              the spend ledger. Exact under Org/Repo/Team/PR paths; withheld
              with the reason under every other grouping — same grammar as the
              cost and savings cells. */}
          <TableCell className={classes.num}>
            {node.review ? node.review.findings : reviewDash(node)}
          </TableCell>
          <TableCell className={classes.num}>
            {node.review ? node.review.fixed : reviewDash(node)}
          </TableCell>
          <TableCell className={classes.num}>
            {node.review ? (
              node.review.fixRate !== undefined ? (
                `${Math.round(node.review.fixRate * 100)}%`
              ) : (
                <Tooltip title="No tracked findings with review activity in this period — nothing to rate, which is not a 0%.">
                  <span tabIndex={0} className={classes.quiet}>—</span>
                </Tooltip>
              )
            ) : (
              reviewDash(node)
            )}
          </TableCell>
          <TableCell className={classes.num}>
            {/* vs-prev compares token totals, which are withheld through a
                method subtree -- a delta on a withheld figure would be noise. */}
            {label && !node.tokensWithheldBy ? (
              <span
                className={
                  delta?.isNew || !delta?.pct
                    ? classes.quiet
                    : delta.pct > 0
                    ? classes.up
                    : classes.down
                }
              >
                {label}
              </span>
            ) : (
              <span className={classes.quiet}>—</span>
            )}
          </TableCell>
          <TableCell className={classes.numQuiet}>
            {node.tokensWithheldBy ? withheldTokensDash : formatTokens(node.totals.inputTokens)}
          </TableCell>
          <TableCell className={classes.numQuiet}>
            {node.tokensWithheldBy ? withheldTokensDash : formatTokens(node.totals.outputTokens)}
          </TableCell>
          <TableCell className={classes.numQuiet}>
            {node.tokensWithheldBy ? withheldTokensDash : formatTokens(node.totals.cacheWriteTokens)}
          </TableCell>
          <TableCell className={classes.numQuiet}>
            {node.tokensWithheldBy ? withheldTokensDash : formatTokens(node.totals.cacheReadTokens)}
          </TableCell>
          <TableCell className={classes.num}>
            {node.tokensWithheldBy ? withheldTokensDash : node.totals.msgs}
          </TableCell>
          <TableCell className={classes.num}>
            {node.runCount !== undefined ? (
              node.runCount
            ) : (
              <Tooltip title="Withheld, not missing: this group spans more than one model and the run-grain records that count a run once across models did not arrive with this payload. Add Model to the grouping for per-model counts.">
                {/* tabIndex: the explanation must exist for keyboard users too --
                    MUI Tooltip opens on focus. */}
                <span tabIndex={0} className={classes.quiet}>mixed</span>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
        {drillOpen && (
          <TableRow>
            <TableCell
              colSpan={15}
              className={classes.reviewDrillCell}
              style={{ paddingLeft: depth * 24 + 40 }}
            >
              <RunFindingsTable
                filters={runFiltersFor(constraints)}
                scope={reviewScope as NonNullable<typeof reviewScope>}
                tz={tz}
                teamFilter={constraints.find(c => c.dim === 'team')?.value}
                teamOf={teamOf}
                findings={findingsForPath(reviewFindings, constraints)}
              />
            </TableCell>
          </TableRow>
        )}
        {!isCollapsed && node.children.map(child => renderRow(child, depth + 1, constraints))}
      </React.Fragment>
    );
  };

  return (
    <>
        <HowItWorks
          collapseKey="claudeUsage.howItWorks"
          // Collapsed on first visit: the page must open with the ANSWER, not the
          // plumbing. Expanding once persists, per the shared component's contract.
          defaultOpen={false}
          lead={
            <>
              Every pr-review run reports its own token usage: the session's end
              hook writes per-model counts, and a separate workflow step ships
              them to Backstage over SQS. The send is best-effort by design — a
              failure logs one line and the check stays green — so the Reporting
              status card at the bottom of this page, not CI, is where silence
              becomes visible.
            </>
          }
          steps={[
            {
              title: 'Capture',
              body:
                'At session end, a hook aggregates per-model tokens (messages, ' +
                'input/output, cache read/write) from the run transcript. The ' +
                'Claude step itself never holds AWS credentials.',
            },
            {
              title: 'Transport',
              body:
                'A later workflow step assumes an AWS role via OIDC (no stored ' +
                'keys) and sends one SQS message per run. Backstage long-polls ' +
                'the queue — there is no inbound endpoint.',
            },
            {
              title: 'Aggregate & show',
              body:
                'The consumer validates the org allow-list, stores raw per-run ' +
                'rows for 12 weeks and weekly rollups forever, and rolls the ' +
                'received week up the moment a message lands. Costs are the ' +
                "runs' own reported metering — no price table lives in this " +
                'tool. Savings are reported the same way: a run that avoided ' +
                'spend names each savings method and its figure, and only ' +
                'reported savings ever show (absence is absence, not zero). ' +
                'Savings dollars are estimated cost avoidance, declared by ' +
                'the producing run — uses are measured by an enforcement ' +
                "hook, and each is priced at that run's own average turn " +
                'cost, so the estimate rides the real metering. Savings ' +
                'count only intervention-attributable methods (counted ' +
                'uses); ambient pricing effects like prompt-cache show ' +
                'separately as a small dollar note beside the cache-read ' +
                'total — a vendor discount the cost column already reflects ' +
                '— and are never summed in. ' +
                'Day and month views ' +
                "read the raw rows in your browser's timezone; weeks are ISO " +
                'weeks in UTC. Visible to admins only.',
            },
          ]}
          footer={
            <Typography variant="caption" color="textSecondary">
              Sources: pr-review runs in m42-platform and m42-games · SQS queue
              svc-backstage-claude-metrics
            </Typography>
          }
        />

        {/* The command rail: period instrument | grouping | actions. */}
        <Box className={classes.rail}>
          <span className={classes.railZone}>
            <span className={classes.navCluster}>
              <Tooltip title={`Earlier ${period.granularity}`}>
                {/* span: a disabled button fires no events, and Tooltip needs a target */}
                <span>
                  <IconButton
                    size="small"
                    disabled={
                      period.granularity === 'week'
                        ? offset >= maxOffset
                        : period.granularity === 'day'
                        ? !dayFloor || period.day <= dayFloor
                        : !monthFloor || period.month <= monthFloor
                    }
                    onClick={() =>
                      setPeriod(p =>
                        p.granularity === 'week'
                          ? { ...p, offset: Math.min(maxOffset, p.offset + 1) }
                          : p.granularity === 'day'
                          ? { ...p, day: shiftDay(p.day, -1) }
                          : { ...p, month: shiftMonth(p.month, -1) },
                      )
                    }
                    aria-label={`Earlier ${period.granularity}`}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="h6" component="p" className={classes.weekLabel}>
                {(!data || granularityMismatch) && 'Loading'}
                {!granularityMismatch && (
                  <>
                    {week && `${week.isoYear}-W${String(week.isoWeek).padStart(2, '0')}`}
                    {week && offset === 0 ? ' (current, still filling)' : ''}
                    {dayData && dayData.day}
                    {dayData && dayData.day === today
                      ? ' (today, still filling)'
                      : ''}
                    {monthData && monthData.month}
                    {/* ONE suffix per state. A current month with a mid-month
                        oldestDay is a data set younger than the month (fresh
                        deployment), so the gap folds into the still-filling
                        label; a past month's gap is the prune, named where the
                        month is named rather than in a footnote. */}
                    {monthData && monthData.month === thisMonth
                      ? ` (current, still filling${
                          monthPartialFrom ? ` — data begins ${monthPartialFrom}` : ''
                        })`
                      : ''}
                    {monthData && monthData.month !== thisMonth
                      ? monthPartialFrom
                        ? ` (partial: data begins ${monthPartialFrom})`
                        : ''
                      : ''}
                  </>
                )}
              </Typography>
              <Tooltip title={`Later ${period.granularity}`}>
                <span>
                  <IconButton
                    size="small"
                    disabled={
                      period.granularity === 'week'
                        ? offset === 0
                        : period.granularity === 'day'
                        ? period.day >= today
                        : period.month >= thisMonth
                    }
                    onClick={() =>
                      setPeriod(p =>
                        p.granularity === 'week'
                          ? { ...p, offset: Math.max(0, p.offset - 1) }
                          : p.granularity === 'day'
                          ? { ...p, day: shiftDay(p.day, 1) }
                          : { ...p, month: shiftMonth(p.month, 1) },
                      )
                    }
                    aria-label={`Later ${period.granularity}`}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </span>

            {/* The granularity is a segmented toggle fused into the instrument.
                Month and Day read raw runs (PR dimension available, retention-
                bounded); Week reads the permanent rollups. */}
            <ToggleButtonGroup
              exclusive
              size="small"
              className={classes.segmented}
              value={period.granularity}
              onChange={(_e, v) => v && switchGranularity(v)}
              aria-label="Period granularity"
            >
              <ToggleButton value="month" aria-label="Month">
                Month
              </ToggleButton>
              <ToggleButton value="week" aria-label="Week">
                Week
              </ToggleButton>
              <ToggleButton value="day" aria-label="Day">
                Day
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              variant="outlined"
              size="small"
              label="Jump to date"
              type="date"
              className={classes.control}
              value={dayData ? dayData.day : ''}
              onChange={e => jumpToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: today,
                min: period.granularity !== 'week' ? dayFloor : undefined,
                'aria-label': 'Jump to date',
              }}
            />
            {/* The reading zone sits IN the period instrument: it defines where day
                and month boundaries fall, so it belongs with the period controls
                rather than with grouping. Compact and label-less to keep the rail on
                one row -- the tooltip and aria-label carry the name, and the period
                header states the zone in full. Weeks are ISO/UTC, so it goes disabled
                there rather than silently doing nothing. */}
            <Tooltip
              /* Emptied while the list is open, not merely un-triggered: the click that
                 opens the list follows a hover, so the tip is ALREADY showing and
                 disabling its listeners leaves it sitting on top of the options it was
                 describing. An empty title renders nothing, whatever hover did. */
              title={
                tzOpen
                  ? ''
                  : period.granularity === 'week'
                  ? 'ISO weeks are always cut in UTC, so the zone does not apply here'
                  : 'The zone day and month boundaries are cut in. UTC matches how the Anthropic Console buckets days.'
              }
            >
              <span>
                <Autocomplete
                  className={classes.tzPicker}
                  classes={{ popper: classes.tzPopper, option: classes.tzOption }}
                  size="small"
                  options={tzOptions()}
                  /* Two different labels on purpose. The INPUT gets the
                     abbreviation, so the trigger stays four characters wide and can
                     never truncate; the LIST gets the full zone name, because
                     'America/Chicago' is what a reader recognises and 'CDT' is not
                     enough to choose from 400 of them. */
                  getOptionLabel={z => tzShortName(z)}
                  renderOption={z => tzOptionLabel(z)}
                  /* Filtering must follow the LIST label, not the input's: with the
                     default, typing 'Chicago' matched nothing because the option's
                     label was the abbreviation. */
                  filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter(
                      z =>
                        z.toLowerCase().includes(q) ||
                        tzShortName(z).toLowerCase().includes(q),
                    );
                  }}
                  value={tz}
                  disableClearable
                  disabled={period.granularity === 'week'}
                  onChange={(_e, v) => v && changeTz(v)}
                  onOpen={() => setTzOpen(true)}
                  onClose={() => setTzOpen(false)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <PublicIcon className={classes.tzGlobe} aria-hidden />
                        ),
                      }}
                      inputProps={{
                        ...params.inputProps,
                        'aria-label': 'Time zone',
                      }}
                    />
                  )}
                />
              </span>
            </Tooltip>
          </span>

          <span className={classes.railZone}>
            {/* Searchable pickers, the house Autocomplete idiom (teamAdmin): the
                dimension list grows with the data model, and a combobox keeps every
                option findable by typing. Both stay non-clearable: level 0 always
                has a value, level 1 clears through the explicit 'nothing' option. */}
            <Autocomplete
              className={classes.picker}
              size="small"
              // Every dimension stays pickable here; a model <-> savings-method
              // conflict is resolved by setPrimary dropping the secondary. Only
              // the SECONDARY picker hides the conflicting option, because a
              // secondary pick has nothing it may legitimately drop.
              options={[...availableDims]}
              getOptionLabel={d => DIMENSION_LABELS[d]}
              value={dims[0]}
              disableClearable
              onChange={(_e, v) => v && setPrimary(v)}
              renderInput={params => (
                <TextField {...params} size="small" variant="outlined" label="Group by" />
              )}
            />
            <Autocomplete<Dimension | 'none', false, true, false>
              className={classes.picker}
              size="small"
              options={[
                'none',
                ...availableDims.filter(
                  d =>
                    d !== dims[0] &&
                    !(d === 'model' && dims[0] === 'savingsMethod') &&
                    !(d === 'savingsMethod' && dims[0] === 'model'),
                ),
              ]}
              getOptionLabel={v => (v === 'none' ? 'nothing' : DIMENSION_LABELS[v])}
              value={dims[1] ?? 'none'}
              disableClearable
              onChange={(_e, v) => v && setSecondary(v)}
              renderInput={params => (
                <TextField {...params} size="small" variant="outlined" label="then" />
              )}
            />
          </span>

          <span className={classes.railZone}>
            <Tooltip title="Refresh the data on this page">
              <span>
                <IconButton
                  size="small"
                  disabled={loading}
                  onClick={() => {
                    // Explicit refresh: full stale presentation, unlike the poll.
                    pollRef.current = false;
                    setReload(n => n + 1);
                  }}
                  aria-label="Refresh"
                >
                  <RefreshIcon className={loading ? classes.spinning : undefined} />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              startIcon={<GetAppIcon />}
              // Also disabled mid-reload: with stale rows on screen, an export
              // would stamp the OLD period's data with a click made for the new.
              disabled={loading || !exportCtx || !visible.groups.length}
              onClick={() =>
                exportCtx &&
                download(
                  exportFilename('shown', exportCtx.derivation),
                  toCsvShownView(visible.groups, dims, exportCtx, deltas),
                )
              }
            >
              Export shown view
            </Button>
            <Button
              size="small"
              startIcon={<GetAppIcon />}
              disabled={loading || !exportCtx || !rows.length}
              onClick={() =>
                exportCtx &&
                download(
                  exportFilename('grain', exportCtx.derivation),
                  toCsvGrain(rows, exportCtx),
                )
              }
            >
              Export full grain
            </Button>
          </span>
        </Box>

        {/* Active narrowing: its own line, present only when something is narrowed. */}
        {focus.length > 0 && (
          <Box className={classes.bar}>
            {focus.map(f => (
              <Chip
                key={`${f.dim}:${f.value}`}
                size="small"
                variant="outlined"
                label={`${DIMENSION_LABELS[f.dim]}: ${
                  actorish(f.dim) && f.value === UNKNOWN_ACTOR ? 'unattributed' : f.value
                }`}
                onDelete={() =>
                  setFocus(current =>
                    current.filter(x => !(x.dim === f.dim && x.value === f.value)),
                  )
                }
              />
            ))}
            {focus.length > 1 && (
              <Link component="button" variant="body2" onClick={() => setFocus([])}>
                Clear all
              </Link>
            )}
          </Box>
        )}

        {/* One polite announcement per recompute: without this, narrowing and
            period changes are visually obvious and aurally nonexistent. */}
        <div aria-live="polite" className={classes.srOnly}>
          {data && grand
            ? `${formatTokens(grand.totalTokens)} tokens across ${
                derivation?.grainRows ?? 0
              } grain rows for ${derivation?.period}${
                focus.length ? `, narrowed by ${focus.length} filter(s)` : ''
              }${
                rowFilter.trim()
                  ? `, row filter matching ${visible.matchedTop} of ${visible.totalTop} groups`
                  : ''
              }`
            : ''}
        </div>

        {/* The bare progress bar appears only when there is nothing stale to hold on
            screen (first load, or recovery after an error cleared the data). Every
            other reload keeps the previous answer visible and dims it instead. */}
        {loading && !data && <Progress />}

        {error && (
          <WarningPanel severity="error" title="Could not load usage metrics">
            {error}
          </WarningPanel>
        )}

        {data && !error && (
          <Box
            aria-busy={loading}
            className={`${classes.content}${
              reloadingStale ? ` ${classes.reloading}` : ''
            }`}
          >
            {grand && (
              <InfoCard
                title={
                  <Typography variant="h6" component="h2">
                    {`Totals for ${derivation?.period}`}
                    {focus.length > 0 && (
                      <Typography component="span" variant="body2" color="textSecondary">
                        {' — narrowed to '}
                        {focus
                          .map(f => `${DIMENSION_LABELS[f.dim]}: ${
                            actorish(f.dim) && f.value === UNKNOWN_ACTOR
                              ? 'unattributed'
                              : f.value
                          }`)
                          .join(', ')}
                      </Typography>
                    )}
                  </Typography>
                }
              >
                <Box className={classes.summaryPrimary}>
                  <Metric
                    label="total tokens"
                    value={<AnimatedNumber value={grand.totalTokens} format={formatTokens} />}
                  />
                  {grandReported &&
                    !('blockedBy' in grandReported) &&
                    grandReported.costedRuns > 0 && (
                      <Metric
                        label="reported cost"
                        value={<AnimatedNumber value={grandReported.usd} format={formatUsd} />}
                        hint={
                          grandReported.costedRuns < grandReported.totalRuns
                            ? `covers ${grandReported.costedRuns} of ${grandReported.totalRuns} runs`
                            : 'from the runs’ own metering'
                        }
                      />
                    )}
                  {/* Withheld is SHOWN, not vanished: the tile disappearing under a
                      model narrowing read as a glitch. Same grammar as 'mixed'. */}
                  {grandReported &&
                    'blockedBy' in grandReported &&
                    data &&
                    !('blockedBy' in reportedCost(data.runCosts, [])) &&
                    (reportedCost(data.runCosts, []) as { costedRuns: number })
                      .costedRuns > 0 && (
                      <Metric
                        label="reported cost"
                        value="—"
                        hint={`withheld under ${DIMENSION_LABELS[grandReported.blockedBy as Dimension] ?? grandReported.blockedBy} narrowing — a run’s cost cannot be split there`}
                      />
                    )}
                  {/* The debounce's yield: review rounds cancelled before any
                      model spend. Absence is absence (no superseded rounds, no
                      tile — data predating the debounce never shows "0%"), and
                      the avoided figure is an ESTIMATE priced at this view's
                      mean completed-review cost, starred like every estimate. */}
                  {grandSuperseded && !('blockedBy' in grandSuperseded) && (
                    <Metric
                      label="superseded rounds"
                      value={
                        <Tooltip
                          title={`Review rounds cancelled by the synchronize quiet period before any model spend (job ${SUPERSEDED_JOB}). The avoided figure is an estimate — cancelled rounds x this view's mean completed-review cost${
                            grandSuperseded.meanReviewCostUsd !== undefined
                              ? ` (${formatUsd(grandSuperseded.meanReviewCostUsd)}/run)`
                              : ''
                          } — not metered dollars.`}
                        >
                          <span tabIndex={0}>
                            <AnimatedNumber
                              value={grandSuperseded.share * 100}
                              format={formatShare}
                            />
                          </span>
                        </Tooltip>
                      }
                      hint={
                        grandSuperseded.estAvoidedUsd !== undefined
                          ? `${grandSuperseded.superseded} of ${grandSuperseded.triggeredRuns} triggered rounds — est. ${formatUsd(grandSuperseded.estAvoidedUsd)} avoided*`
                          : `${grandSuperseded.superseded} of ${grandSuperseded.triggeredRuns} triggered rounds — unpriced (no completed round carries a cost to price against)`
                      }
                    />
                  )}
                  {/* Withheld is SHOWN here too, same grammar as the cost tile. */}
                  {grandSuperseded &&
                    'blockedBy' in grandSuperseded &&
                    allSuperseded &&
                    !('blockedBy' in allSuperseded) && (
                      <Metric
                        label="superseded rounds"
                        value="—"
                        hint={`withheld under ${DIMENSION_LABELS[grandSuperseded.blockedBy as Dimension] ?? grandSuperseded.blockedBy} narrowing — runs cannot be split there`}
                      />
                    )}
                  {/* Savings mirror the cost tile's grammar: shown with coverage
                      when reported, withheld-with-reason under a model narrowing,
                      and simply absent when nothing reported one (absence of a
                      report is not a measured zero). The per-method strip sits
                      INSIDE the tile's column so the categories read as this
                      figure's breakdown, not the band's. */}
                  {grandSavings &&
                    !('blockedBy' in grandSavings) &&
                    // Coverage-only narrowings render NOTHING here: their $0 is
                    // "reached these runs", not a measured zero — the same rule
                    // the table's counted-gate applies. A counted method's
                    // honest $0.00 still shows.
                    grandSavings.methods.some(m => !isCoverageMethod(m)) && (
                      <Box display="flex" flexDirection="column">
                        <Metric
                          label="reported savings"
                          value={<AnimatedNumber value={grandSavings.usd} format={formatUsd} />}
                          hint={
                            // Attributable-only figure now, so the hint's run
                            // coverage must be the attributable population; the
                            // server computes it (method run counts overlap), and
                            // a pre-field cell withholds the claim rather than
                            // letting this approximate one.
                            grandSavings.savedRuns === undefined
                              ? 'run coverage withheld (older data in range)'
                              : grandSavings.savedRuns < grandSavings.totalRuns
                                ? `from ${grandSavings.savedRuns} of ${grandSavings.totalRuns} runs`
                                : `from all ${grandSavings.totalRuns} runs`
                          }
                        />
                        {grandSavings.methods.length > 0 && (
                          <div className={classes.methodRows} aria-label="savings by method">
                            {grandSavings.methods.slice(0, 4).map(m => (
                              <React.Fragment key={m.method}>
                                {/* tabIndex: the explanation must exist for
                                    keyboard users too — Tooltip opens on focus. */}
                                <Tooltip title={describeSavingsMethod(m.method)}>
                                  <span tabIndex={0} className={classes.methodName}>
                                    {m.method}
                                  </span>
                                </Tooltip>
                                {/* A coverage method prices nothing on purpose.
                                    Its $0.00 sitting beside real dollars reads as
                                    "delivered nothing" when it means "these runs
                                    were reached". */}
                                {isCoverageMethod(m) ? (
                                  <Tooltip title="Coverage, not savings: this row reports how many runs the method reached, not dollars. It is deliberately unpriced — it fires on every review, including the many that would never have run a query, so pricing each one as an avoided query would invent savings that never happened.">
                                    <span tabIndex={0} className={classes.methodCoverage}>
                                      covered
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <span className={classes.methodUsd}>{formatUsd(m.usd)}</span>
                                )}
                                <span className={classes.methodRuns}>
                                  {m.runs} run{m.runs === 1 ? '' : 's'}
                                </span>
                                {/* Use count: how often the method actually fired.
                                    Counting is optional per producer — starred when
                                    only some of the runs counted, blank (never a
                                    guessed zero) when none did. */}
                                {m.uses !== undefined && m.countedRuns !== undefined ? (
                                  m.countedRuns < m.runs ? (
                                    <Tooltip
                                      title={`${m.uses} uses counted by ${m.countedRuns} of ${m.runs} runs — the rest reported no count.`}
                                    >
                                      <span tabIndex={0} className={classes.methodRuns}>
                                        {m.uses} uses*
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <span className={classes.methodRuns}>
                                      {m.uses} use{m.uses === 1 ? '' : 's'}
                                    </span>
                                  )
                                ) : (
                                  <span className={classes.methodRuns}>—</span>
                                )}
                              </React.Fragment>
                            ))}
                            {grandSavings.methods.length > 4 && (
                              <span className={classes.methodMore}>
                                +{grandSavings.methods.length - 4} more — group by Savings for the full split
                              </span>
                            )}
                          </div>
                        )}
                      </Box>
                    )}
                  {grandSavings &&
                    'blockedBy' in grandSavings &&
                    allSavings &&
                    !('blockedBy' in allSavings) &&
                    (allSavings.methods.length > 0 || allSavings.ambient.usd > 0) && (
                      <Metric
                        label="reported savings"
                        value="—"
                        hint={`withheld under ${DIMENSION_LABELS[grandSavings.blockedBy as Dimension] ?? grandSavings.blockedBy} narrowing — a run’s savings cannot be split there`}
                      />
                    )}
                  {weekDelta && (
                    <Metric
                      label={`vs previous ${data.granularity}`}
                      value={weekDelta.text}
                      tone={weekDelta.up ? 'up' : 'down'}
                      // The percentage stays static: its baseline changes with it, and
                      // two numbers rolling in one tile reads as noise, not motion.
                      hint={`from ${formatTokens(weekDelta.baseline)} total tokens`}
                    />
                  )}
                </Box>
                <Box className={classes.summarySecondary}>
                  <Metric secondary label="input" value={formatTokens(grand.inputTokens)} />
                  <Metric secondary label="output" value={formatTokens(grand.outputTokens)} />
                  <Metric
                    secondary
                    label="cache write"
                    value={formatTokens(grand.cacheWriteTokens)}
                  />
                  <Metric
                    secondary
                    label="cache read"
                    value={
                      // The prompt-cache delta rides HERE, beside the traffic
                      // that earned it, deliberately small: it is a vendor
                      // pricing effect the cost figure already reflects — an
                      // annotation on cache reads, never a headline tile and
                      // never savings (reclassified 2026-08-26).
                      grandSavings &&
                      !('blockedBy' in grandSavings) &&
                      grandSavings.ambient.usd > 0 ? (
                        <>
                          {formatTokens(grand.cacheReadTokens)}
                          {/* Per-method sentences: the cache claim is only
                              ever made about prompt-cache — a future priced-
                              but-uncounted method lands here too (the
                              structural rule's documented caveat) and must
                              describe itself, not borrow cache semantics. */}
                          <Tooltip
                            title={grandSavings.ambient.methods
                              .map(m =>
                                m.method === 'prompt-cache'
                                  ? `prompt-cache across ${m.runs} run${m.runs === 1 ? '' : 's'}: saved against fresh-input pricing, net of write premiums, for the runs that reported it within this total — a vendor pricing effect already reflected in reported cost, not savings this tooling created. Floored at zero per run, so it reads high across many runs.`
                                  : `${m.method} (${formatUsd(m.usd)} across ${m.runs} run${m.runs === 1 ? '' : 's'}): priced without counted uses, so it is excluded from reported savings. ${describeSavingsMethod(m.method)}`,
                              )
                              .join(' ')}
                          >
                            <span tabIndex={0} className={classes.cacheDiscountNote}>
                              {` · ${formatUsd(grandSavings.ambient.usd)} off`}
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        formatTokens(grand.cacheReadTokens)
                      )
                    }
                  />
                  <Metric secondary label="messages" value={String(grand.msgs)} />
                  <Metric
                    secondary
                    label="source rows"
                    value={String(derivation?.grainRows ?? 0)}
                    hint="behind these totals"
                  />
                </Box>
              </InfoCard>
            )}

            <Box mt={2.5}>
              <InfoCard
                title={
                  <Typography variant="h6" component="h2">
                    Breakdown
                  </Typography>
                }
                subheader={derivation?.grouping}
                action={
                  <TextField
                    variant="outlined"
                    size="small"
                    className={classes.picker}
                    placeholder="Filter rows"
                    value={rowFilter}
                    onChange={e => setRowFilter(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Escape') setRowFilter('');
                    }}
                    inputProps={{ 'aria-label': 'Filter rows by any value' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: rowFilter ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear row filter"
                            onClick={() => setRowFilter('')}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                }
              >
                {/* Spend and outcome share one frame (PLAT-1819): the scope's
                    headline quality figures sit above the table they summarize. */}
                {reviewData && (
                  <ReviewQualitySummaryStrip
                    findings={reviewFindings}
                    withheldReason={
                      reviewWithhold ? reviewWithheldReason(reviewWithhold) : undefined
                    }
                    droppedRows={reviewData.droppedRows}
                    rowFilterActive={rowFilter.trim() !== ''}
                  />
                )}
                {groups.length === 0 ? (
                  <Typography color="textSecondary">
                    {allRows.length === 0
                      ? `No usage recorded for this ${data.granularity}.`
                      : focus.length
                      ? 'Nothing matches the current narrowing. Remove a chip above to widen it.'
                      : `No usage recorded for this ${data.granularity}.`}
                    {quiet.some(s => s.stale)
                      ? ' Some repos have gone quiet: see Reporting status below.'
                      : ''}
                    {/* A genuinely quiet local day still reads as breakage
                        without a way forward -- offer the latest day with data. */}
                    {!focus.length &&
                      dayData?.latestDay &&
                      dayData.latestDay !== dayData.day && (
                        <>
                          {' '}
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() =>
                              setPeriod({
                                granularity: 'day',
                                day: dayData.latestDay as string,
                              })
                            }
                          >
                            View {dayData.latestDay} — the latest day with data
                          </Link>
                        </>
                      )}
                    {/* Same affordance one level up: an empty month offers the
                        latest month that has data. */}
                    {!focus.length &&
                      monthData?.latestDay &&
                      monthOfDay(monthData.latestDay) !== monthData.month && (
                        <>
                          {' '}
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() =>
                              setPeriod({
                                granularity: 'month',
                                month: monthOfDay(monthData.latestDay as string),
                              })
                            }
                          >
                            View {monthOfDay(monthData.latestDay)} — the latest month
                            with data
                          </Link>
                        </>
                      )}
                  </Typography>
                ) : visible.groups.length === 0 ? (
                  <Typography color="textSecondary">
                    {`Nothing matches “${rowFilter.trim()}” in the current grouping. `}
                    <Link component="button" variant="body2" onClick={() => setRowFilter('')}>
                      Clear the filter
                    </Link>
                  </Typography>
                ) : (
                  <div key={viewKey} className={`${classes.scroll} ${classes.settleIn}`}>
                    <Table size="small" className={classes.table}>
                      <caption className={classes.srOnly}>
                        Usage grouped by {dims.map(d => DIMENSION_LABELS[d]).join(' then ')}
                      </caption>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {dims.map(d => DIMENSION_LABELS[d]).join(' / ')}
                          </TableCell>
                          <TableCell>Team</TableCell>
                          <SortHeader label="Total" k="total" />
                          <SortHeader
                            label="Cost"
                            k="cost"
                            title="The runs' own reported metering, counted once per distinct run. Withheld where a group splits runs by model — a run's cost has no per-model share. No price table lives in this tool."
                          />
                          <SortHeader
                            label="Savings"
                            k="savings"
                            title="Estimated cost avoidance from intervention-attributable methods only (counted uses), declared by the reporting runs and counted once per distinct run. Ambient pricing effects (prompt-cache) are excluded from every group figure — except the prompt-cache row itself under a Savings-method grouping — since that vendor discount already lives in the cost column and is annotated beside the cache-read total. Uses are measured; dollars are each run's own declared estimate, priced at its own metering — never a price table. Blank where no run in the group reported a counted method, and “covered” where the method reports coverage rather than dollars. Group by Savings method for the per-method split."
                          />
                          <SortHeader
                            label="Findings"
                            k="findings"
                            title="Review findings whose last review activity landed in this period, from the pr-review fixes-log (advisory suggestions counted apart, in the drill-down). Withheld under Model, PR author, Run trigger, Run type and Savings groupings — findings carry none of those; group by Org, Repo, Team or PR for exact figures."
                          />
                          <TableCell className={classes.num}>
                            <Tooltip title="Findings with fix evidence (a commit the review re-verified, or /pr-fix).">
                              <span tabIndex={0}>Fixed</span>
                            </Tooltip>
                          </TableCell>
                          <SortHeader
                            label="Fix rate"
                            k="fixRate"
                            title="Fixed ÷ tracked findings — the addressed rate. Blank where the group tracked nothing this period (not a 0%)."
                          />
                          <TableCell className={classes.num}>
                            <Tooltip title="Change in total tokens against the same group in the previous period. Blank when there is nothing comparable.">
                              <span tabIndex={0}>vs prev</span>
                            </Tooltip>
                          </TableCell>
                          <SortHeader label="In" k="input" />
                          <SortHeader label="Out" k="output" />
                          <SortHeader label="Cache w" k="cacheWrite" />
                          <SortHeader label="Cache r" k="cacheRead" />
                          <SortHeader label="Msgs" k="msgs" />
                          <SortHeader
                            label="Runs"
                            k="runs"
                            title="Distinct runs in the group -- a run reporting several models is counted once, from run-grain records. On Model rows a run counts toward each model it used, which is why Runs never sums across models."
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody className={classes.focusBody}>
                        {visible.groups.map(g => renderRow(g, 0, []))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {rowFilter.trim() !== '' && visible.groups.length > 0 && (
                  <Box mt={1}>
                    <Typography variant="caption" color="textSecondary">
                      {visible.matchedTop} of {visible.totalTop} top-level groups match
                      {' — hidden rows are pruned for display; every figure shown is '}
                      still that group's full total.
                    </Typography>
                  </Box>
                )}
              </InfoCard>
            </Box>

            <Box mt={2.5}>
              {/* An emitter that stopped reporting looks exactly like a quiet week in the
                  numbers above, so its age is stated rather than implied. */}
              <InfoCard
                title={
                  <Typography variant="h6" component="h2">
                    Reporting status
                  </Typography>
                }
                subheader={reportingStatus}
              >
                <Box className={classes.statusSplit}>
                <Box className={classes.statusTableZone}>
                {quiet.length === 0 ? (
                  <Typography color="textSecondary">
                    No repo has ever reported usage. If pr-review is running, the emitter is
                    not reaching Backstage.
                  </Typography>
                ) : (
                  <div className={classes.scroll}>
                  <Table
                    size="small"
                    className={`${classes.table} ${classes.statusTable}`}
                  >
                    <caption className={classes.srOnly}>
                      Last received message per repo, stalest first
                    </caption>
                    <TableHead>
                      <TableRow>
                        <TableCell>Repo</TableCell>
                        <TableCell className={classes.num}>Last received</TableCell>
                        <TableCell className={classes.num}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quiet.map(s => (
                        <TableRow key={`${s.org}/${s.repo}`}>
                          <TableCell>
                            {s.org}/{s.repo}
                            {/* The diagnosis path: a quiet repo's next question is
                                "why" -- its Actions runs are where the answer lives. */}
                            <Link
                              className={classes.prLink}
                              href={githubUrl(s.org, s.repo, 'actions')}
                              target="_blank"
                              rel="noopener"
                              aria-label={`Open ${s.org}/${s.repo} workflow runs on GitHub`}
                            >
                              <OpenInNewIcon />
                            </Link>
                          </TableCell>
                          <TableCell className={classes.num}>
                            {s.receivedAt ? (
                              s.receivedAt.toISOString().slice(0, 10)
                            ) : (
                              <span className={classes.quiet}>over 12 weeks ago</span>
                            )}
                          </TableCell>
                          <TableCell className={classes.num}>
                            {s.stale ? (
                              <Tooltip
                                title={`No message from this repo in ${
                                  Number.isFinite(s.ageDays)
                                    ? `${Math.floor(s.ageDays)} days`
                                    : 'over 12 weeks'
                                } — anything past 8 days has missed at least one weekly reporting window. Check the repo's workflow runs (link beside the name).`}
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  className={classes.quietChip}
                                  tabIndex={0}
                                  label={
                                    Number.isFinite(s.ageDays)
                                      ? `quiet ${Math.floor(s.ageDays)}d`
                                      : 'no recent data'
                                  }
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                reporting
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
                </Box>
                {/* The chart survives table degradation on purpose: with no
                    repo ever reporting, thirty flat alarm ticks ARE the
                    honest picture. Absent activity data (older server, failed
                    read) renders nothing -- the hook already reported it. */}
                {activityDays && (
                  <ActivityChart
                    tz={tz}
                    days={activityDays}
                    // Click-to-jump: an explicit action, so it takes the full
                    // stale-dim treatment like any other period change.
                    onPickDay={day => setPeriod({ granularity: 'day', day })}
                  />
                )}
                </Box>
              </InfoCard>
            </Box>

          </Box>
        )}
    </>
  );
};

/** Owning team for a repo node, distinguishing "no owner" from "lookup failed". */
const OwnerCell = ({
  node,
  owners,
  failed,
}: {
  node: GroupNode;
  owners: ReadonlyMap<string, string>;
  failed: boolean;
}) => {
  const classes = useStyles();
  // Exact key lookup, one per org the node spans. Suffix-matching `*_/repo` was both wrong
  // and slow: repo names are not unique across orgs, so `.find()` returned whichever key
  // happened to come first and could attribute one org's repo to another org's team -- a
  // wrong answer on a chargeback surface. It also scanned the whole catalog map per row.
  const found = node.orgs
    .map(org => owners.get(`${org}/${node.value}`.toLowerCase()))
    .filter((o): o is string => Boolean(o));
  const unique = [...new Set(found)];
  if (unique.length) {
    // displayOwner, not a local slice-at-the-first-slash: that shortened
    // `group:other-ns/team` to `team`, silently dropping a non-default namespace. The two
    // agree on `group:default/online`, which is why the divergence never surfaced -- and the
    // tested behaviour was the one no render reached.
    // More than one only when the node spans orgs owning the same repo name differently;
    // naming both beats picking one.
    return <>{unique.map(o => displayOwner(o) ?? o).join(', ')}</>;
  }
  return (
    <Tooltip
      title={
        failed
          ? 'The catalog lookup failed, so ownership is unknown for every repo'
          : 'No Component in the catalog carries this repo in github.com/project-slug'
      }
    >
      <Typography variant="caption" className={classes.quiet}>
        {failed ? '-' : 'not in catalog'}
      </Typography>
    </Tooltip>
  );
};
